<!-- eslint-disable vue/valid-v-slot -->
<template>
  <div class="payouts-page">
      <!-- Page header -->
      <div class="page-header">
        <div class="page-header-text">
          <h1 class="page-title">Wypłaty kierowców</h1>
        </div>
      </div>

      <!-- Segmented tabs -->
      <div class="tabs-pill" role="tablist">
        <button
          class="tab-pill"
          :class="{ 'tab-pill--active': activeTab === 0 }"
          @click="activeTab = 0"
        >
          <v-icon size="16">mdi-cash-multiple</v-icon>
          <span>{{ $t('payouts.title') }}</span>
        </button>
        <button
          class="tab-pill"
          :class="{ 'tab-pill--active': activeTab === 1 }"
          @click="activeTab = 1"
        >
          <v-icon size="16">mdi-receipt</v-icon>
          <span>{{ $t('payouts.receiptsTab') }}</span>
        </button>
        <button
          class="tab-pill"
          :class="{ 'tab-pill--active': activeTab === 2 }"
          @click="activeTab = 2"
        >
          <v-icon size="16">mdi-cash-fast</v-icon>
          <span>Wypłaty BLIK</span>
        </button>
        <button
          class="tab-pill"
          :class="{ 'tab-pill--active': activeTab === 3 }"
          @click="activeTab = 3"
        >
          <v-icon size="16">mdi-cash-minus</v-icon>
          <span>Wnioski podflot</span>
          <span class="tab-badge" v-if="pendingCharges.length > 0">{{ pendingCharges.length }}</span>
        </button>
      </div>

      <!-- touchless: zmiana zakładki tylko klikiem, bez przewijania gestem w prawo/lewo -->
      <v-tabs-items v-model="activeTab" touchless>
        <!-- TAB 1: Wypłaty -->
        <v-tab-item>
      <!-- Filter Section -->
      <div class="filter-section mb-4">
        <div class="filter-section-header">
          <div class="filter-icon"><v-icon size="18" color="#5478FF">mdi-filter-variant</v-icon></div>
          <div class="filter-title">{{ $t('payouts.filters') }}</div>
        </div>
        <v-row>
          <v-col cols="12" md="3">
            <v-select
              v-model="filters.subCompany"
              :items="subCompanies"
              :label="$t('payouts.branch')"
              outlined dense hide-details
              @change="initialize"
              multiple
              item-text="name"
              item-value="id"
              prepend-inner-icon="mdi-domain"
            ></v-select>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              :label="$t('payouts.week')"
              v-model="filters.selectedWeek"
              type="week"
              outlined dense hide-details
              prepend-inner-icon="mdi-calendar-week"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="filters.settlementType"
              :items="settlementTypes"
              :label="$t('payouts.settlementType')"
              outlined dense hide-details
              prepend-inner-icon="mdi-cash-multiple"
              @change="initialize"
            ></v-select>
          </v-col>
        </v-row>
      </div>

      <!-- Cost Invoice Pool Panel -->
      <div v-if="poolInfo.poolTotal > 0" class="pool-panel mb-4">
        <div class="pool-panel-header">
          <div class="pool-icon"><v-icon size="20" color="#5478FF">mdi-cash-multiple</v-icon></div>
          <div class="pool-title">{{ $t('payouts.costInvoicePool') }}</div>
          <button class="pool-expand-btn" @click="poolExpanded = !poolExpanded">
            {{ poolExpanded ? $t('payouts.collapse') : $t('payouts.expand') }}
            <v-icon size="16">{{ poolExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </button>
        </div>
        <div class="pool-grid">
          <div class="pool-stat pool-stat--blue">
            <div class="pool-stat-icon"><v-icon size="18" color="#5478FF">mdi-wallet</v-icon></div>
            <div class="pool-stat-label">{{ $t('payouts.pool') }}</div>
            <div class="pool-stat-value">{{ formatCurrency(poolInfo.poolTotal) }}</div>
          </div>
          <div class="pool-stat pool-stat--orange">
            <div class="pool-stat-icon"><v-icon size="18" color="#FFB74D">mdi-arrow-decision</v-icon></div>
            <div class="pool-stat-label">{{ $t('payouts.allocated') }}</div>
            <div class="pool-stat-value">{{ formatCurrency(poolInfo.totalAllocated) }}</div>
          </div>
          <div class="pool-stat pool-stat--green">
            <div class="pool-stat-icon"><v-icon size="18" color="#2DCB73">mdi-cash-check</v-icon></div>
            <div class="pool-stat-label">{{ $t('payouts.remaining') }}</div>
            <div class="pool-stat-value">{{ formatCurrency(poolInfo.remaining) }}</div>
          </div>
          <div class="pool-stat pool-stat--grey">
            <div class="pool-stat-icon"><v-icon size="18" color="#90A4AE">mdi-file-document-multiple</v-icon></div>
            <div class="pool-stat-label">{{ $t('payouts.invoiceCount') }}</div>
            <div class="pool-stat-value">{{ poolInfo.invoiceCount }}</div>
          </div>
        </div>
        <div class="pool-body">

          <!-- Active Allocations List -->
          <v-expand-transition>
            <div v-if="poolExpanded && poolInfo.allocations && poolInfo.allocations.length > 0">
              <v-divider class="my-3"></v-divider>
              <div class="subtitle-2 mb-2">{{ $t('payouts.activeAllocations') }}:</div>
              <v-simple-table dense>
                <thead>
                  <tr>
                    <th>{{ $t('common.driver') }}</th>
                    <th>{{ $t('payouts.amount') }}</th>
                    <th>{{ $t('payouts.admin') }}</th>
                    <th>{{ $t('common.date') }}</th>
                    <th>{{ $t('payouts.action') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="alloc in poolInfo.allocations" :key="alloc.id">
                    <td>{{ alloc.driverName }}</td>
                    <td>{{ formatCurrency(alloc.amount) }}</td>
                    <td>{{ alloc.adminName }}</td>
                    <td>{{ new Date(alloc.createdAt).toLocaleString('pl-PL') }}</td>
                    <td>
                      <v-btn x-small color="error" @click="revertPoolAllocation(alloc.id)">
                        <v-icon x-small class="mr-1">mdi-undo</v-icon>{{ $t('payouts.revert') }}
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
          </v-expand-transition>
        </div>
      </div>

      <!-- Editable Data Table -->
      <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading"
        :search="search"
        :options.sync="options"
        :custom-sort="customSort"
        :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
        class="elevation-1"
        dense
        outlined
        editable
        show-select
        v-model="selectedRows"
        show-expand
        single-expand
        :expanded.sync="expandedRows"
        item-key="id"
        :item-class="rowClass"
        @item-expanded="onRowExpanded"
      >
        <template v-slot:top>
          <div class="bulk-bar">
            <button
              v-if="selectedRows.length"
              class="action-pill action-pill--warning"
              :disabled="!cashBatchReady"
              :title="cashBatchHint"
              @click="markAsPaidCashBatch()"
            >
              <v-icon size="14">mdi-cash-multiple</v-icon>
              Wypłać gotówką razem ({{ selectedRows.length }})
              <span v-if="cashBatchReady"> — {{ formatGroszy(cashBatchTotal) }}</span>
            </button>
            <button class="action-pill action-pill--info" @click="publishAll()" :disabled="publishingAll || !hasUnpublished">
              <v-icon size="14">mdi-eye</v-icon>{{ $t('payouts.publishAll') }}
            </button>
            <button class="action-pill action-pill--neutral" @click="unpublishAll()">
              <v-icon size="14">mdi-eye-off</v-icon>{{ $t('payouts.unpublishAll') }}
            </button>
            <button class="action-pill action-pill--error" @click="revertAll()">
              <v-icon size="14">mdi-undo</v-icon>{{ $t('payouts.revertAll') }}
            </button>
            <button class="action-pill action-pill--success" @click="markAllWirePaid()">
              <v-icon size="14">mdi-bank-transfer</v-icon>{{ $t('payouts.wiresPaid') }}
            </button>
            <button class="action-pill action-pill--warning" @click="finalizeAll()">
              <v-icon size="14">mdi-check-all</v-icon>{{ $t('payouts.allPaid') }}
            </button>
            <button class="action-pill action-pill--primary" @click="transferDialog = true">
              <v-icon size="14">mdi-bank-transfer</v-icon>{{ $t('payouts.generateBaskets') }}
            </button>
            <button class="action-pill action-pill--warning" @click="openOnDemand()">
              <v-icon size="14">mdi-flash</v-icon>Na żądanie
            </button>
          </div>
          <div class="search-row">
            <div class="search-input">
              <v-icon size="18" color="#6E7691">mdi-magnify</v-icon>
              <input
                v-model="search"
                type="text"
                :placeholder="$t('common.search')"
              />
            </div>
          </div>

        </template>
        <template v-slot:item.driver="{ item }">
          <div class="d-flex align-center" style="gap: 6px; flex-wrap: wrap;">
            <v-chip v-if="item.isSubFleetOwner" x-small color="deep-purple" dark>
              <v-icon x-small left>mdi-domain</v-icon>WŁAŚCICIEL PODFLOTY
            </v-chip>
            <a v-if="item.driverId" href="#" @click.prevent="goToDriver(item.driverId)"
               style="text-decoration: underline; cursor: pointer; color: #1976d2;">
              {{ item.driver }}
            </a>
            <span v-else>{{ item.driver }}</span>
            <span v-if="item.isSubFleetOwner" class="caption grey--text">
              ({{ item.subFleetName }} • {{ item.carCount }} aut)
            </span>
          </div>
        </template>
        <template v-slot:item.netPayout="{ item }">
          <template v-if="item.cooperationTypeRaw === 'B2B_VAT' || item.cooperationTypeRaw === 'B2B_NO_VAT'">
            <v-chip small color="blue" text-color="white">{{ $t('payouts.invoiceBased') }}</v-chip>
            {{ item.netPayout }}
          </template>
          <template v-else>
            {{ item.netPayout }}
          </template>
        </template>
        <template v-slot:item.costInvoiceReimbursement="{ item }">
          <template v-if="item.cooperationTypeRaw === 'B2B_VAT' || item.cooperationTypeRaw === 'B2B_NO_VAT'">
            -
          </template>
          <template v-else>
            {{ item.costInvoiceReimbursement }}
          </template>
        </template>
        <template v-slot:item.rentTransferAmount="{ item }">
          <template v-if="item.cooperationTypeRaw === 'B2B_VAT' || item.cooperationTypeRaw === 'B2B_NO_VAT'">
            -
          </template>
          <template v-else>
            {{ item.rentTransferAmount }}
          </template>
        </template>
        <template v-slot:item.invoiceStatus="{ item }">
          <template v-if="item.isB2B">
            <div class="d-flex align-center">
              <template v-if="item.isInvoiceUploaded && item.invoiceStatusB2B === 'PENDING'">
                <v-chip small color="orange" text-color="white" class="mr-1">{{ $t('payouts.invoicePendingReview') }}</v-chip>
                <v-btn v-if="item.invoiceFileId" x-small color="blue" dark class="mr-1" @click="previewInvoice(item.invoiceFileId)">
                  <v-icon x-small>mdi-eye</v-icon>
                </v-btn>
                <v-btn x-small color="green" dark class="mr-1" :title="$t('payouts.acceptInvoice')" @click="reviewInvoice(item, 'ACCEPT')">
                  <v-icon x-small>mdi-check</v-icon>
                </v-btn>
                <v-btn x-small color="red" dark :title="$t('payouts.rejectInvoice')" @click="reviewInvoice(item, 'REJECT')">
                  <v-icon x-small>mdi-close</v-icon>
                </v-btn>
              </template>
              <template v-else-if="item.isInvoiceUploaded">
                <v-chip small color="green" text-color="white" class="mr-1">{{ $t('payouts.invoiceAccepted') }}</v-chip>
                <v-btn v-if="item.invoiceFileId" x-small color="blue" dark class="mr-1" @click="previewInvoice(item.invoiceFileId)">
                  <v-icon x-small>mdi-eye</v-icon>
                </v-btn>
              </template>
              <template v-else>
                <v-chip v-if="item.invoiceStatusB2B === 'REJECTED'" small color="red" text-color="white" class="mr-1" :title="item.invoiceRejectReason">{{ $t('payouts.invoiceRejected') }}</v-chip>
                <v-btn v-if="item.invoiceStatusB2B === 'REJECTED' && item.invoiceFileId" x-small color="blue" dark class="mr-1" @click="previewInvoice(item.invoiceFileId)">
                  <v-icon x-small>mdi-eye</v-icon>
                </v-btn>
                <v-btn x-small color="orange" dark class="mr-1" @click="markInvoiceUploaded(item.id)">
                  <v-icon x-small class="mr-1">mdi-check</v-icon>{{ $t('payouts.markInvoice') }}
                </v-btn>
                <v-btn x-small color="primary" dark @click="openUploadInvoiceDialog(item.id)">
                  <v-icon x-small class="mr-1">mdi-file-upload</v-icon>{{ $t('payouts.uploadInvoice') }}
                </v-btn>
              </template>
            </div>
          </template>
          <template v-else>-</template>
        </template>
        <template v-slot:item.bailiffInfo="{ item }">
          <template v-if="item.bailiffName">
            <v-chip x-small color="red" text-color="white" class="mr-1">{{ item.bailiffName }}</v-chip>
            <div class="caption">{{ item.bailiffTypes }}</div>
          </template>
          <template v-else>-</template>
        </template>
        <template v-slot:item.actions="{ item }">
          <div class="d-flex">
            <v-btn x-small class="error mr-1" @click="revertPayout(item.id)" :title="$t('payouts.revert')">
              <v-icon x-small>mdi-undo</v-icon>
            </v-btn>
            <v-btn
              v-if="!item.isSettledRaw && !(item.isB2B && !(item.isInvoiceUploaded && item.invoiceStatusB2B === 'ACCEPTED')) && item.payoutTypeRaw !== 'CASH'"
              x-small
              class="success mr-1"
              :loading="rowBusy[item.id] === 'markPaid'"
              :disabled="!!rowBusy[item.id]"
              @click="markAsPaid(item.id)"
              :title="$t('payouts.paidWire')"
            >
              <v-icon x-small>mdi-check</v-icon>
            </v-btn>
            <v-btn
              v-if="!item.isSettledRaw && !(item.isB2B && !(item.isInvoiceUploaded && item.invoiceStatusB2B === 'ACCEPTED')) && item.payoutTypeRaw === 'CASH'"
              x-small
              class="warning mr-1"
              :loading="rowBusy[item.id] === 'markPaidCash'"
              :disabled="!!rowBusy[item.id]"
              @click="markAsPaidCash(item.id)"
              :title="$t('payouts.paidCash')"
            >
              <v-icon x-small>mdi-cash</v-icon>
            </v-btn>
            <v-btn
              v-if="!item.isPublishedRaw && !item.isSettledRaw"
              x-small
              class="info mr-1"
              :loading="rowBusy[item.id] === 'publish'"
              :disabled="!!rowBusy[item.id]"
              @click="publishSettlement(item.id)"
              :title="$t('payouts.publish')"
            >
              <v-icon x-small>mdi-eye</v-icon>
            </v-btn>
            <v-btn
              v-if="item.isPublishedRaw && !item.isSettledRaw"
              x-small
              class="secondary"
              :loading="rowBusy[item.id] === 'unpublish'"
              :disabled="!!rowBusy[item.id]"
              @click="unpublishSettlement(item.id)"
              :title="$t('payouts.unpublish')"
            >
              <v-icon x-small>mdi-eye-off</v-icon>
            </v-btn>
            <v-btn
              v-if="isEligibleForPool(item) && poolInfo.remaining > 0"
              x-small
              color="purple"
              dark
              class="ml-1"
              @click="openAllocationDialog(item)"
              :title="$t('payouts.allocateFromPool')"
            >
              <v-icon x-small>mdi-cash-plus</v-icon>
            </v-btn>
          </div>
        </template>

        <!-- Rozwijany wiersz: per-aplikacja + korekty (lazy load) -->
        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="pa-0" style="background: #FAFAFA;">
            <div v-if="breakdownLoading[item.id]" class="pa-4 text-center">
              <v-progress-circular indeterminate size="22" color="primary"></v-progress-circular>
            </div>
            <div v-else-if="breakdownData[item.id]" class="pa-4">
              <div class="d-flex flex-wrap" style="gap: 24px;">
                <!-- Per-aplikacja -->
                <div style="min-width: 320px; flex: 1;">
                  <div class="text-overline grey--text">Zarobki per aplikacja</div>
                  <table class="breakdown-table">
                    <thead>
                      <tr>
                        <th>Aplikacja</th>
                        <th class="text-right">Kursy</th>
                        <th class="text-right">Brutto</th>
                        <th class="text-right">Netto</th>
                        <th class="text-right">Gotówka</th>
                        <th v-if="breakdownData[item.id].totalBonus > 0" class="text-right">Bonus / Quest</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="p in breakdownData[item.id].platforms" :key="p.platform">
                        <td><b>{{ platformLabel(p.platform) }}</b></td>
                        <td class="text-right">{{ p.orders }}</td>
                        <td class="text-right">{{ formatGroszy(p.gross) }}</td>
                        <td class="text-right">{{ formatGroszy(p.net) }}</td>
                        <td class="text-right">{{ formatGroszy(p.cash) }}</td>
                        <td v-if="breakdownData[item.id].totalBonus > 0" class="text-right blue--text">{{ p.bonus ? formatGroszy(p.bonus) : '—' }}</td>
                      </tr>
                      <tr v-if="!breakdownData[item.id].platforms.length">
                        <td :colspan="breakdownData[item.id].totalBonus > 0 ? 6 : 5" class="text-center grey--text">Brak danych z aplikacji w tym rozliczeniu.</td>
                      </tr>
                      <tr class="totals-row">
                        <td><b>RAZEM</b></td>
                        <td class="text-right">{{ breakdownData[item.id].totals.orders }}</td>
                        <td class="text-right">{{ formatGroszy(breakdownData[item.id].totals.gross) }}</td>
                        <td class="text-right">{{ formatGroszy(breakdownData[item.id].totals.net) }}</td>
                        <td class="text-right">{{ formatGroszy(breakdownData[item.id].totals.cash) }}</td>
                        <td v-if="breakdownData[item.id].totalBonus > 0" class="text-right blue--text">{{ formatGroszy(breakdownData[item.id].totalBonus) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <!-- Wypłata podfloty w rozbiciu na auta -->
                <div
                  v-for="sf in (breakdownData[item.id].subFleetPayouts || [])"
                  :key="sf.bonusId"
                  style="min-width: 420px; flex: 1 1 100%;"
                >
                  <div class="text-overline grey--text">
                    Podflota {{ sf.subFleetName }} — {{ sf.periodKey }}
                  </div>
                  <table class="breakdown-table">
                    <thead>
                      <tr>
                        <th>Auto</th>
                        <th>Kierowca</th>
                        <th class="text-right">Pobrany najem</th>
                        <th class="text-right">Nasza prowizja</th>
                        <th class="text-right">Do wypłaty</th>
                        <th class="text-right">Postój</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="a in sf.perCar" :key="sf.bonusId + a.plate">
                        <td class="font-weight-medium">{{ a.plate }}</td>
                        <td class="grey--text">{{ (a.drivers || []).join(', ') || '—' }}</td>
                        <td class="text-right">{{ formatGroszy(a.rentCollected) }}</td>
                        <td class="text-right red--text">−{{ formatGroszy(a.commission) }}</td>
                        <td class="text-right green--text">{{ formatGroszy(a.netPayout) }}</td>
                        <td class="text-right" :class="a.idleDays ? 'amber--text' : 'grey--text'">
                          <span v-if="a.idleDays">{{ a.idleDays }} dni / −{{ formatGroszy(a.idleLost) }}</span>
                          <span v-else>—</span>
                        </td>
                      </tr>
                      <tr v-if="!sf.perCar.length">
                        <td colspan="6" class="text-center grey--text">Brak rozbicia na auta dla tego okresu.</td>
                      </tr>
                      <tr class="font-weight-bold">
                        <td colspan="2">Razem</td>
                        <td class="text-right">{{ formatGroszy(sf.rentCollected) }}</td>
                        <td class="text-right red--text">−{{ formatGroszy(sf.feeAmount) }}</td>
                        <td class="text-right green--text">{{ formatGroszy(sf.netPayout) }}</td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                  <div class="text-caption grey--text mt-1">
                    „Postój" to dni, w których auto nie miało najemcy, i czynsz, którego przez to nie było.
                  </div>
                </div>

                <!-- Korekty / odliczenia -->
                <div style="min-width: 320px; flex: 1;">
                  <div class="text-overline grey--text">Korekty i odliczenia</div>
                  <table class="breakdown-table">
                    <tbody>
                      <tr v-if="breakdownData[item.id].settlement.zusDeduction">
                        <td>ZUS</td>
                        <td class="text-right red--text">−{{ formatGroszy(breakdownData[item.id].settlement.zusDeduction) }}</td>
                      </tr>
                      <tr v-if="breakdownData[item.id].settlement.administrationTax">
                        <td>Opłata administracyjna</td>
                        <td class="text-right red--text">−{{ formatGroszy(breakdownData[item.id].settlement.administrationTax) }}</td>
                      </tr>
                      <tr v-if="breakdownData[item.id].settlement.settlementCost">
                        <td>Koszt rozliczenia <span class="caption grey--text">(WEEKLY 50zł / EVERY_2_WEEKS 70zł / MONTH 100zł / DAILY 30zł)</span></td>
                        <td class="text-right red--text">−{{ formatGroszy(breakdownData[item.id].settlement.settlementCost) }}</td>
                      </tr>
                      <tr v-if="breakdownData[item.id].settlement.dailyAdvancesDeducted">
                        <td>Zaliczki dzienne (odjęte)</td>
                        <td class="text-right red--text">−{{ formatGroszy(breakdownData[item.id].settlement.dailyAdvancesDeducted) }}</td>
                      </tr>
                      <tr v-if="breakdownData[item.id].settlement.dailyPayoutFeesCollected">
                        <td>Opłaty za wypłaty dzienne</td>
                        <td class="text-right red--text">−{{ formatGroszy(breakdownData[item.id].settlement.dailyPayoutFeesCollected) }}</td>
                      </tr>
                      <tr v-if="breakdownData[item.id].settlement.excludedDriverReturn">
                        <td>Zwrot wykluczony <span class="caption grey--text">(dla CONNECTION/RENT — informacyjnie)</span></td>
                        <td class="text-right grey--text">{{ formatGroszy(breakdownData[item.id].settlement.excludedDriverReturn) }}</td>
                      </tr>
                      <tr v-for="(c, ci) in (breakdownData[item.id].appNegativeCorrections || [])" :key="'neg'+ci">
                        <td>Korekta {{ platformLabel(c.platform) }} <span class="caption grey--text">(ujemne netto z pliku rozliczeniowego)</span></td>
                        <td class="text-right red--text">{{ formatGroszy(c.netto) }}</td>
                      </tr>
                      <tr v-if="rentTransferFor(item.id) > 0">
                        <td>Czynsz najmu (przelew)</td>
                        <td class="text-right">{{ formatGroszy(rentTransferFor(item.id)) }}</td>
                      </tr>
                      <tr v-if="breakdownData[item.id].settlement.zleceniowkaAmount">
                        <td>Zleceniówka (przelew)</td>
                        <td class="text-right">{{ formatGroszy(breakdownData[item.id].settlement.zleceniowkaAmount) }}</td>
                      </tr>
                      <tr v-if="breakdownData[item.id].settlement.costInvoiceReimbursement">
                        <td>Zwrot kosztów z faktur</td>
                        <td class="text-right green--text">+{{ formatGroszy(breakdownData[item.id].settlement.costInvoiceReimbursement) }}</td>
                      </tr>
                      <tr v-if="breakdownData[item.id].settlement.vatReimbursement">
                        <td>Zwrot VAT (8% z netto)</td>
                        <td class="text-right green--text">+{{ formatGroszy(breakdownData[item.id].settlement.vatReimbursement) }}</td>
                      </tr>
                      <tr v-for="b in breakdownData[item.id].settlement.bonuses" :key="b.id">
                        <td>Bonus: {{ b.name }} <span class="caption grey--text">({{ b.type }})</span></td>
                        <td class="text-right" :class="isBonusDeduction(b) ? 'red--text' : 'green--text'">
                          {{ isBonusDeduction(b) ? '−' : '+' }}{{ formatGroszy(Math.abs(b.price)) }}
                        </td>
                      </tr>
                      <tr v-for="i in breakdownData[item.id].settlement.incomes" :key="i.id">
                        <td>Dochód: {{ i.name }}</td>
                        <td class="text-right">{{ formatGroszy(i.driverReturn) }}</td>
                      </tr>
                      <tr v-if="breakdownData[item.id].settlement.zusDeduction">
                        <td class="caption grey--text">↳ wartość brutto przed ZUS (z ZUS-em)</td>
                        <td class="text-right caption grey--text">
                          {{ formatGroszy(breakdownData[item.id].settlement.totalToPayout + breakdownData[item.id].settlement.zusDeduction) }}
                        </td>
                      </tr>
                      <tr class="totals-row">
                        <td><b>Łącznie do wypłaty</b></td>
                        <td class="text-right"><b>{{ formatGroszy(breakdownData[item.id].settlement.totalToPayout) }}</b></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div v-else class="pa-4 caption red--text">
              Nie udało się załadować szczegółów.
            </div>
          </td>
        </template>
      </v-data-table>

      <!-- Podsumowanie przelewów per koszyk (respektuje filtr harmonogramu) -->
      <v-card v-if="transferSummary" outlined class="mb-3" style="background:#232838;">
        <v-card-text class="py-2">
          <div class="d-flex align-center mb-1">
            <v-icon small left color="primary">mdi-bank-transfer</v-icon>
            <span class="subtitle-2">Podsumowanie przelewów</span>
            <span class="caption grey--text ml-2">({{ filters.settlementType ? filters.settlementType : 'wszystkie harmonogramy' }})</span>
            <v-spacer></v-spacer>
            <v-progress-circular v-if="loadingSummary" indeterminate size="16" width="2" color="primary"></v-progress-circular>
          </div>
          <v-simple-table dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th>Koszyk</th>
                  <th class="text-right">Kwota</th>
                  <th class="text-right">Przelewów</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>Zleceniówka</td><td class="text-right">{{ formatGroszy(transferSummary.categories.zleceniowka) }}</td><td class="text-right">{{ transferSummary.counts.zleceniowka }}</td></tr>
                <tr><td>Zwrot kosztów</td><td class="text-right">{{ formatGroszy(transferSummary.categories.zwrot_kosztow) }}</td><td class="text-right">{{ transferSummary.counts.zwrot_kosztow }}</td></tr>
                <tr v-if="transferSummary.vatFromInvoices != null" style="opacity:.85">
                  <td class="pl-6">— w tym realny zwrot VAT z faktur</td>
                  <td class="text-right green--text text--lighten-2">{{ formatGroszy(transferSummary.vatFromInvoices) }}</td>
                  <td class="text-right">{{ transferSummary.vatFromInvoicesCount }}</td>
                </tr>
                <tr><td>Najem</td><td class="text-right">{{ formatGroszy(transferSummary.categories.najem) }}</td><td class="text-right">{{ transferSummary.counts.najem }}</td></tr>
                <tr><td>Komornik</td><td class="text-right">{{ formatGroszy(transferSummary.categories.komornik) }}</td><td class="text-right">{{ transferSummary.counts.komornik }}</td></tr>
                <tr><td>Zaliczki dzienne</td><td class="text-right">{{ formatGroszy(transferSummary.categories.zaliczki_dzienne) }}</td><td class="text-right">{{ transferSummary.counts.zaliczki_dzienne }}</td></tr>
                <tr class="font-weight-bold" style="border-top:2px solid #3a4152;">
                  <td>Razem</td>
                  <td class="text-right primary--text">{{ formatGroszy(transferSummary.grandTotal) }}</td>
                  <td class="text-right">{{ totalTransferCount }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>
      </v-card>

      <!-- Zaległe doliczone do sum: niewypłacone rozliczenia z wcześniejszych
           tygodni (każdy typ współpracy; B2B tylko z zaakceptowaną fakturą) -->
      <v-alert
        v-if="transferSummary && transferSummary.lateB2B && transferSummary.lateB2B.count"
        dense text type="info" class="mb-3"
      >
        W kwotach ujęte <b>zaległe, niewypłacone rozliczenia z wcześniejszych tygodni</b>
        (np. opublikowane po tamtych przelewach; B2B — faktura zaakceptowana po
        wypłatach): {{ transferSummary.lateB2B.count }} szt.
        — przelewy: <b>{{ formatGroszy(transferSummary.lateB2B.wire) }}</b>,
        gotówka: <b>{{ formatGroszy(transferSummary.lateB2B.cash) }}</b>.
        Bez nich suma tygodnia = całość minus te kwoty.
      </v-alert>

      <!-- Które rozliczenia zalegają i jak długo. Sama liczba sztuk nie mówiła
           nic o tym, że coś wisi trzeci tydzień — a wisząca pozycja z zajęciem
           komorniczym oznaczała kolejny przelew do komornika za ten sam okres. -->
      <v-card
        v-if="transferSummary && transferSummary.lateDetails && transferSummary.lateDetails.length"
        outlined class="mb-3" style="background:#232838;"
      >
        <v-card-text class="py-2">
          <div class="d-flex align-center mb-2">
            <v-icon small left color="warning">mdi-clock-alert-outline</v-icon>
            <span class="subtitle-2">Zaległe rozliczenia — czekają na oznaczenie</span>
            <v-spacer></v-spacer>
            <span class="caption grey--text">{{ transferSummary.lateDetails.length }} szt.</span>
          </div>
          <v-simple-table dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">Kierowca</th>
                  <th class="text-left">Tydzień</th>
                  <th class="text-left">Forma</th>
                  <th class="text-right">Kwota</th>
                  <th class="text-right">Czeka</th>
                  <th class="text-left">Komornik</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, i) in transferSummary.lateDetails" :key="i">
                  <td>{{ row.driverName }}</td>
                  <td class="caption grey--text">{{ row.week }}</td>
                  <td>
                    <span v-if="row.isCash" class="caption">
                      <v-icon x-small color="grey" class="mr-1">mdi-cash</v-icon>gotówka
                    </span>
                    <span v-else class="caption">
                      <v-icon x-small color="grey" class="mr-1">mdi-bank-transfer</v-icon>przelew
                    </span>
                  </td>
                  <td class="text-right">{{ formatGroszy(row.amountCents) }}</td>
                  <td class="text-right">
                    <span :class="lateDaysClass(row.daysWaiting, row.isCash)">{{ row.daysWaiting }} dni</span>
                  </td>
                  <td>
                    <template v-if="row.hasBailiff">
                      <v-icon x-small :color="row.bailiffAlreadySent ? 'success' : 'warning'" class="mr-1">
                        {{ row.bailiffAlreadySent ? 'mdi-check-circle-outline' : 'mdi-alert-outline' }}
                      </v-icon>
                      <span class="caption">
                        {{ row.bailiffName }}
                        <span class="grey--text">
                          — {{ row.bailiffAlreadySent ? 'przelew już wysłany' : 'przelew jeszcze nie wyszedł' }}
                        </span>
                      </span>
                    </template>
                    <span v-else class="caption grey--text">—</span>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
          <div class="caption grey--text mt-2">
            Rozliczenie wraca do kolejnych paczek, dopóki nie zostanie oznaczone jako wypłacone.
            <b>Gotówkowe</b> przechodzą na kolejny tydzień, aż kierowca odbierze pieniądze — to
            normalne. Zwróć uwagę na <b>przelewowe</b> wiszące ponad tydzień. Część komornicza
            wychodzi tylko raz (oznaczona w ostatniej kolumnie), więc podwójny przelew już nie grozi.
          </div>
        </v-card-text>
      </v-card>

      <!-- Podsumowanie wypłat gotówkowych per koszyk (kierowcy bez konta / gotówka) -->
      <v-card v-if="transferSummary && transferSummary.cash" outlined class="mb-3" style="background:#232838;">
        <v-card-text class="py-2">
          <div class="d-flex align-center mb-1">
            <v-icon small left color="success">mdi-cash-multiple</v-icon>
            <span class="subtitle-2">Podsumowanie wypłat gotówkowych</span>
            <span class="caption grey--text ml-2">({{ filters.settlementType ? filters.settlementType : 'wszystkie harmonogramy' }})</span>
            <v-spacer></v-spacer>
            <v-progress-circular v-if="loadingSummary" indeterminate size="16" width="2" color="success"></v-progress-circular>
          </div>
          <v-simple-table dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th>Koszyk</th>
                  <th class="text-right">Kwota</th>
                  <th class="text-right">Wypłat</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>Zleceniówka</td><td class="text-right">{{ formatGroszy(transferSummary.cash.categories.zleceniowka) }}</td><td class="text-right">{{ transferSummary.cash.counts.zleceniowka }}</td></tr>
                <tr><td>Zwrot kosztów</td><td class="text-right">{{ formatGroszy(transferSummary.cash.categories.zwrot_kosztow) }}</td><td class="text-right">{{ transferSummary.cash.counts.zwrot_kosztow }}</td></tr>
                <tr><td>Najem</td><td class="text-right">{{ formatGroszy(transferSummary.cash.categories.najem) }}</td><td class="text-right">{{ transferSummary.cash.counts.najem }}</td></tr>
                <tr><td>Zaliczki dzienne</td><td class="text-right">{{ formatGroszy(transferSummary.cash.categories.zaliczki_dzienne) }}</td><td class="text-right">{{ transferSummary.cash.counts.zaliczki_dzienne }}</td></tr>
                <tr class="font-weight-bold" style="border-top:2px solid #3a4152;">
                  <td>Razem</td>
                  <td class="text-right success--text">{{ formatGroszy(transferSummary.cash.grandTotal) }}</td>
                  <td class="text-right">{{ totalCashCount }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
          <div class="caption grey--text mt-1">Kwoty do wypłaty gotówką. Komornik zawsze przelewem — nie jest tu ujęty.</div>
        </v-card-text>
      </v-card>

      <!-- Allocation Dialog -->
      <v-dialog v-model="allocationDialog.show" max-width="500">
        <v-card>
          <v-card-title>{{ $t('payouts.allocateFromPool') }}</v-card-title>
          <v-card-text>
            <div class="mb-3">
              <strong>{{ $t('common.driver') }}:</strong> {{ allocationDialog.driverName }}
            </div>
            <div class="mb-3">
              <strong>{{ $t('payouts.remainingInPool') }}:</strong> {{ formatCurrency(poolInfo.remaining) }}
            </div>
            <div class="mb-3">
              <strong>{{ $t('payouts.currentCostReimbursement') }}:</strong> {{ allocationDialog.currentCostReimbursement }}
            </div>
            <div class="mb-3">
              <strong>{{ $t('payouts.currentRent') }}:</strong> {{ allocationDialog.currentRentTransfer }}
            </div>
            <v-text-field
              v-model="allocationDialog.amount"
              :label="$t('payouts.amountPLN')"
              type="number"
              :max="poolInfo.remaining / 100"
              min="0.01"
              step="0.01"
              outlined
              dense
              :rules="[v => !!v || 'Podaj kwotę', v => v > 0 || 'Kwota musi być większa od 0', v => v * 100 <= poolInfo.remaining || 'Kwota przekracza dostępną pulę']"
            ></v-text-field>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="allocationDialog.show = false">{{ $t('common.cancel') }}</v-btn>
            <v-btn color="purple" dark @click="submitAllocation()">{{ $t('payouts.allocate') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Upload Invoice Dialog -->
      <v-dialog v-model="uploadInvoiceDialog" max-width="500">
        <v-card>
          <v-card-title>{{ $t('payouts.uploadInvoiceTitle') }}</v-card-title>
          <v-card-text>
            <v-file-input
              v-model="invoiceFile"
              :label="$t('payouts.selectInvoiceFile')"
              outlined
              dense
              accept=".pdf,.jpg,.jpeg,.png"
              prepend-icon="mdi-file-document"
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="uploadInvoiceDialog = false">{{ $t('common.cancel') }}</v-btn>
            <v-btn color="primary" :disabled="!invoiceFile" @click="uploadInvoice()">{{ $t('payouts.upload') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Preview Invoice Dialog -->
      <v-dialog v-model="previewInvoiceDialog" max-width="800">
        <v-card>
          <v-card-title>
            {{ $t('payouts.invoicePreview') }}
            <v-spacer></v-spacer>
            <v-btn icon @click="previewInvoiceDialog = false"><v-icon>mdi-close</v-icon></v-btn>
          </v-card-title>
          <v-card-text class="text-center">
            <v-progress-circular v-if="previewLoading" indeterminate color="primary" />
            <iframe
              v-else-if="previewUrl && previewIsPdf"
              :src="previewUrl"
              style="width: 100%; height: 600px; border: none;"
            />
            <img
              v-else-if="previewUrl"
              :src="previewUrl"
              style="max-width: 100%; max-height: 600px;"
            />
          </v-card-text>
        </v-card>
      </v-dialog>
      <!-- Generate Transfers Dialog -->
      <v-dialog v-model="transferDialog" max-width="600">
        <v-card>
          <v-card-title>{{ $t('payouts.generateTransfersTitle') }}</v-card-title>
          <v-card-text>
            <v-select
              v-model="transferConfig.bankFormat"
              :items="[{text: 'mBank', value: 'MBANK'}, {text: 'PKO BP', value: 'PKO'}]"
              item-text="text"
              item-value="value"
              :label="$t('payouts.bankFormat')"
              outlined
              dense
            />
            <v-select
              v-model="transferConfig.bankAccountId"
              :items="bankAccounts"
              item-text="label"
              item-value="id"
              :label="$t('payouts.senderAccount')"
              outlined
              dense
              @change="onBankAccountSelect"
            />
            <!-- Pole BYŁO `readonly`, więc biuro nie mogło skrócić za długiej
                 nazwy — a to ona 05.10.2026 zablokowała całą paczkę komorniczą.
                 Teraz da się ją poprawić, a licznik pokazuje limit banku
                 (35 znaków na linię) PRZED wysłaniem pliku. -->
            <v-text-field
              v-model="transferConfig.senderName"
              :label="$t('payouts.senderName')"
              outlined
              dense
              counter="35"
              :error="(transferConfig.senderName || '').length > 70"
              :hint="podpowiedzNadawcy"
              persistent-hint
            />
            <div class="caption grey--text mt-2">
              {{ $t('payouts.transferDescription') }}
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="transferDialog = false">{{ $t('common.cancel') }}</v-btn>
            <v-btn
              color="primary"
              :disabled="!transferConfig.bankAccountId || !transferConfig.senderName"
              :loading="generatingTransfers"
              @click="generateTransfers()"
            >{{ $t('payouts.generate') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Dialog: wypłata na żądanie -->
      <v-dialog v-model="onDemandDialog" max-width="520">
        <v-card>
          <v-card-title>
            <v-icon left color="warning">mdi-flash</v-icon>Wypłata na żądanie
          </v-card-title>
          <v-card-text>
            <v-autocomplete
              v-model="onDemandDriverId"
              :items="onDemandDrivers"
              :loading="onDemandLoadingDrivers"
              item-text="name"
              item-value="id"
              label="Kierowca"
              outlined dense clearable
              prepend-inner-icon="mdi-account-search"
            />
            <v-alert dense text type="warning" class="mb-2">
              Za wypłatę na żądanie doliczana jest opłata <b>50 zł</b> (pozycja ujemna w rozliczeniu).
            </v-alert>
            <v-alert dense text type="info" class="mb-0">
              Warunek: zarobki z aplikacji (łącznie z zaległymi okresami) muszą pokrywać
              ZUS i minimalną zleceniówkę. Rozliczenie obejmie okres od poniedziałku do dziś
              plus wszystkie nierozliczone tygodnie.
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text :disabled="onDemandSending" @click="onDemandDialog = false">Anuluj</v-btn>
            <v-btn color="warning" :loading="onDemandSending" :disabled="!onDemandDriverId" @click="submitOnDemand()">
              <v-icon left small>mdi-flash</v-icon>Generuj wypłatę
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Signature Dialog for Cash Payment -->
      <v-dialog v-model="signatureDialog.show" max-width="700" persistent eager>
        <v-card>
          <v-card-title>{{ $t('payouts.cashReceiptTitle') }}</v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12">
                <v-select
                  v-model="cashSubCompanyId"
                  :items="cashSubCompanies"
                  item-text="name"
                  item-value="id"
                  label="Oddział (na który ma trafić wpis kasowy)"
                  outlined
                  dense
                  hide-details="auto"
                  :rules="[v => !!v || 'Wybierz oddział']"
                />
              </v-col>
              <v-col cols="12">
                <v-alert dense text :type="cashDialogKomornik > 0 ? 'error' : 'success'" class="mb-0">
                  <div class="d-flex align-center">
                    <span class="subtitle-1">Do wydania z kasy</span>
                    <v-spacer />
                    <span class="text-h6 font-weight-bold">{{ formatGroszy(cashDialogDoReki) }}</span>
                  </div>
                  <div v-if="cashDialogKomornik > 0" class="caption mt-1">
                    Komornik: {{ formatGroszy(cashDialogKomornik) }} idzie przelewem na konto komornika —
                    <b>tej kwoty nie wydajemy kierowcy</b>.
                  </div>
                </v-alert>
              </v-col>
              <v-col cols="12" v-if="signatureDialog.related.length">
                <v-alert dense text type="warning" class="mb-3">
                  <div class="subtitle-2 mb-1">
                    Ten kierowca ma jeszcze
                    {{ signatureDialog.related.length }}
                    niewypłaconą gotówkę z wcześniejszych tygodni
                  </div>
                  <div
                    v-for="r in signatureDialog.related"
                    :key="r.id"
                    class="d-flex align-center"
                  >
                    <v-checkbox
                      v-model="r.checked"
                      dense
                      hide-details
                      class="mt-0 pt-0 mr-2"
                    />
                    <span class="caption">
                      okres od {{ new Date(r.date).toLocaleDateString('pl-PL') }}
                    </span>
                    <v-spacer />
                    <span class="caption font-weight-medium">{{ formatGroszy(r.amount) }}</span>
                  </div>
                  <div class="d-flex align-center mt-2 pt-2" style="border-top:1px solid rgba(255,255,255,.15)">
                    <span class="subtitle-2">Razem do wypłaty</span>
                    <v-spacer />
                    <span class="subtitle-2 font-weight-bold">{{ formatGroszy(cashDialogTotal) }}</span>
                  </div>
                  <div class="caption mt-1">
                    Zaznaczone wejdą na to samo pokwitowanie, z rozpisaniem okresów.
                  </div>
                </v-alert>
              </v-col>
              <v-col cols="12">
                <v-alert dense text type="info" class="mb-3">
                  <div class="d-flex align-center flex-wrap" style="gap:8px">
                    <v-icon small color="info">mdi-tablet-dashboard</v-icon>
                    <span class="subtitle-2">Podpis na tablecie</span>
                    <v-spacer />
                    <template v-if="signatureDevices.length">
                      <v-btn
                        small color="info" :loading="tabletWaiting"
                        @click="requestTabletSignature()"
                      >
                        <v-icon left small>mdi-send</v-icon>
                        {{ tabletWaiting ? 'Czekam na podpis…' : 'Poproś o podpis na tablecie' }}
                      </v-btn>
                      <v-btn v-if="tabletWaiting" small text @click="cancelTabletSignature()">Anuluj</v-btn>
                    </template>
                    <v-btn v-else small text color="info" @click="startTabletPairing()">
                      <v-icon left small>mdi-plus</v-icon>Sparuj tablet
                    </v-btn>
                  </div>
                  <div v-if="tabletWaiting" class="caption mt-1">
                    Podaj tablet kierowcy — podpis pojawi się tu automatycznie.
                  </div>
                  <div v-if="tabletError" class="caption error--text mt-1">{{ tabletError }}</div>
                  <div v-if="tabletPairing" class="mt-2">
                    <div class="caption">Wpisz ten kod na tablecie (strona <b>/podpis</b>):</div>
                    <div class="text-h4 font-weight-bold" style="letter-spacing:10px">{{ tabletPairing }}</div>
                    <div class="caption">Kod jest jednorazowy i wygasa po 10 minutach.</div>
                  </div>
                </v-alert>
              </v-col>
              <v-col cols="12">
                <v-card class="mb-4" elevation="2">
                  <v-card-title class="text-h6 primary--text font-weight-bold">
                    <v-icon left color="primary">mdi-signature-freehand</v-icon>
                    {{ $t('payouts.driverSignature') }}
                    <v-spacer />
                    <v-btn small text color="error" @click="clearCashSig('driver')">
                      <v-icon left small>mdi-eraser</v-icon>Wyczyść
                    </v-btn>
                  </v-card-title>
                  <v-card-text>
                    <div class="signature-container">
                      <SignatureField
                        width="100%"
                        height="200px"
                        ref="cashDriverSignaturePad"
                        class="signature-pad elevation-1"
                        :options="{ onEnd: () => onCashSigEnd('driver') }"
                      />
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12">
                <v-card class="mb-4" elevation="2">
                  <v-card-title class="text-h6 primary--text font-weight-bold">
                    <v-icon left color="secondary">mdi-signature-freehand</v-icon>
                    {{ $t('payouts.workerSignature') }}
                    <v-spacer />
                    <v-btn small text color="error" @click="clearCashSig('worker')">
                      <v-icon left small>mdi-eraser</v-icon>Wyczyść
                    </v-btn>
                  </v-card-title>
                  <v-card-text>
                    <div class="signature-container">
                      <SignatureField
                        width="100%"
                        height="200px"
                        ref="cashWorkerSignaturePad"
                        class="signature-pad elevation-1"
                        :options="{ onEnd: () => onCashSigEnd('worker') }"
                      />
                    </div>
                    <div class="d-flex align-center mt-2" v-if="myCanUseFacsimile && myFacsimile">
                      <v-btn small outlined color="primary" @click="useFacsimileForWorker">
                        <v-icon left small>mdi-draw</v-icon>Wstaw moją faksymilkę
                      </v-btn>
                      <template v-if="cashSig.worker === myFacsimile">
                        <img :src="myFacsimile" style="max-height:40px; margin-left:12px; background:#fff; border:1px solid #ddd; border-radius:4px;" />
                        <span class="caption green--text ml-2">Faksymilka wstawiona</span>
                      </template>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-btn text @click="signatureDialog.show = false">{{ $t('common.cancel') }}</v-btn>
            <v-spacer></v-spacer>
            <v-btn color="warning" @click="submitCashPayment(false, true)">{{ $t('payouts.saveWithoutSignature') }}</v-btn>
            <v-btn color="secondary" @click="submitCashPayment(false, false)">Zapisz</v-btn>
            <v-btn color="primary" @click="submitCashPayment(true, false)">{{ $t('payouts.saveAndPrint') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

        </v-tab-item>

        <!-- TAB 2: Pokwitowania -->
        <v-tab-item>
          <!-- Receipts Filters -->
          <div class="filter-section mb-4">
            <div class="filter-section-header">
              <div class="filter-icon"><v-icon size="18" color="#5478FF">mdi-filter-variant</v-icon></div>
              <div class="filter-title">{{ $t('payouts.receiptFilters') }}</div>
            </div>
            <v-row>
              <v-col cols="12" md="3">
                <v-select
                  v-model="receipts.filters.subCompany"
                  :items="subCompanies"
                  :label="$t('payouts.branch')"
                  outlined dense hide-details
                  multiple
                  item-text="name"
                  item-value="id"
                  prepend-inner-icon="mdi-domain"
                  @change="fetchReceipts"
                ></v-select>
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  :label="$t('payouts.week')"
                  v-model="receipts.filters.selectedWeek"
                  type="week"
                  outlined dense hide-details
                  prepend-inner-icon="mdi-calendar-week"
                  @change="fetchReceipts"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  v-model="receipts.filters.settlementType"
                  :items="settlementTypes"
                  :label="$t('payouts.settlementType')"
                  outlined dense hide-details
                  prepend-inner-icon="mdi-cash-multiple"
                  @change="fetchReceipts"
                ></v-select>
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="receipts.search"
                  :label="$t('payouts.searchDriver')"
                  outlined dense hide-details
                  prepend-inner-icon="mdi-magnify"
                  @input="fetchReceipts"
                ></v-text-field>
              </v-col>
            </v-row>
          </div>

          <!-- Receipts Table -->
          <v-data-table
            :headers="receipts.headers"
            :items="receipts.items"
            :loading="receipts.loading"
            :options.sync="receipts.options"
            :server-items-length="receipts.total"
            class="elevation-1"
            dense
          >
            <template v-slot:top>
              <div class="table-section-title">
                <div class="filter-icon"><v-icon size="18" color="#5478FF">mdi-receipt</v-icon></div>
                <div class="filter-title">{{ $t('payouts.cashReceipts') }}</div>
              </div>
            </template>
            <template v-slot:item.totalToPayout="{ item }">
              {{ formatCurrency(item.totalToPayout) }}
            </template>
            <template v-slot:item.date="{ item }">
              {{ new Date(item.date).toLocaleDateString('pl-PL') }}
            </template>
            <template v-slot:item.updatedAt="{ item }">
              {{ new Date(item.updatedAt).toLocaleString('pl-PL') }}
            </template>
            <template v-slot:item.cooperationType="{ item }">
              {{ formatCooperationType(item.cooperationType) }}
            </template>
            <template v-slot:item.signatures="{ item }">
              <v-icon v-if="item.hasSignature" small color="green" :title="$t('payouts.driverSigned')">mdi-check-circle</v-icon>
              <v-icon v-else small color="grey" :title="$t('payouts.noDriverSignature')">mdi-close-circle</v-icon>
              <v-icon v-if="item.hasWorkerSignature" small color="green" :title="$t('payouts.workerSigned')" class="ml-1">mdi-check-circle</v-icon>
              <v-icon v-else small color="grey" :title="$t('payouts.noWorkerSignature')" class="ml-1">mdi-close-circle</v-icon>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn x-small color="primary" @click="reprintReceipt(item.id)" :title="$t('payouts.printReceipt')">
                <v-icon x-small>mdi-printer</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-tab-item>

        <!-- TAB 3: Wypłaty BLIK (self-service history) -->
        <v-tab-item>
          <div class="filter-section mb-4">
            <div class="filter-section-header">
              <div class="filter-icon"><v-icon size="18" color="#5478FF">mdi-filter-variant</v-icon></div>
              <div class="filter-title">Filtry</div>
            </div>
            <v-row>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="blik.filters.from"
                  label="Od"
                  type="date"
                  outlined dense hide-details
                  prepend-inner-icon="mdi-calendar-start"
                  @change="fetchBlik"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="blik.filters.to"
                  label="Do"
                  type="date"
                  outlined dense hide-details
                  prepend-inner-icon="mdi-calendar-end"
                  @change="fetchBlik"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  v-model="blik.filters.status"
                  :items="blikStatusOptions"
                  label="Status"
                  outlined dense hide-details
                  clearable
                  prepend-inner-icon="mdi-tag"
                  @change="fetchBlik"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="blik.filters.search"
                  label="Szukaj kierowcy"
                  outlined dense hide-details
                  clearable
                  prepend-inner-icon="mdi-magnify"
                />
              </v-col>
            </v-row>
          </div>

          <div v-if="blikSummary.count > 0" class="summary-grid mb-4">
            <div class="summary-stat">
              <div class="summary-icon" style="background: rgba(41, 98, 255, 0.18);"><v-icon size="18" color="#5478FF">mdi-counter</v-icon></div>
              <div class="summary-label">Zgłoszeń łącznie</div>
              <div class="summary-value">{{ blikSummary.count }}</div>
            </div>
            <div class="summary-stat">
              <div class="summary-icon" style="background: rgba(45, 203, 115, 0.16);"><v-icon size="18" color="#2DCB73">mdi-cash-check</v-icon></div>
              <div class="summary-label">Wypłacone (netto)</div>
              <div class="summary-value" style="color: #6FE89A;">{{ formatCurrency(blikSummary.totalAmount) }}</div>
            </div>
            <div class="summary-stat">
              <div class="summary-icon" style="background: rgba(167, 139, 250, 0.16);"><v-icon size="18" color="#A78BFA">mdi-piggy-bank</v-icon></div>
              <div class="summary-label">Opłaty BTP</div>
              <div class="summary-value">{{ formatCurrency(blikSummary.totalFee) }}</div>
            </div>
            <div class="summary-stat">
              <div class="summary-icon" style="background: rgba(255, 152, 0, 0.16);"><v-icon size="18" color="#FFB74D">mdi-sigma</v-icon></div>
              <div class="summary-label">Razem z opłatą</div>
              <div class="summary-value">{{ formatCurrency(blikSummary.totalDeducted) }}</div>
            </div>
            <div class="summary-stat">
              <div class="summary-icon" style="background: rgba(45, 203, 115, 0.16);"><v-icon size="18" color="#2DCB73">mdi-check-circle</v-icon></div>
              <div class="summary-label">Zrealizowanych</div>
              <div class="summary-value" style="color: #6FE89A;">{{ blikSummary.paid }}</div>
            </div>
            <div class="summary-stat">
              <div class="summary-icon" style="background: rgba(244, 67, 54, 0.16);"><v-icon size="18" color="#EF5350">mdi-close-circle</v-icon></div>
              <div class="summary-label">Odrzuconych / błędów</div>
              <div class="summary-value" style="color: #FF8A80;">{{ blikSummary.rejected }}</div>
            </div>
          </div>

          <v-data-table
            :headers="blik.headers"
            :items="blikFiltered"
            :loading="blik.loading"
            :items-per-page="25"
            class="elevation-1"
            dense
            no-data-text="Brak wypłat BLIK w wybranym okresie"
          >
            <template v-slot:top>
              <div class="table-section-title">
                <div class="filter-icon"><v-icon size="18" color="#5478FF">mdi-cash-fast</v-icon></div>
                <div class="filter-title">Wypłaty BLIK — historia</div>
                <button class="action-pill action-pill--neutral" @click="fetchBlik" :disabled="blik.loading">
                  <v-icon size="14">mdi-refresh</v-icon>Odśwież
                </button>
              </div>
            </template>
            <template v-slot:item.driver="{ item }">
              {{ item.driver ? (item.driver.firstName + ' ' + item.driver.lastName) : '—' }}
            </template>
            <template v-slot:item.phone="{ item }">
              {{ (item.driver && item.driver.phone) || '—' }}
            </template>
            <template v-slot:item.requestedAmount="{ item }">
              <span v-if="isBlikPaid(item)">
                <strong class="success--text">{{ formatCurrency(item.requestedAmountCents) }}</strong>
              </span>
              <span v-else class="grey--text text-decoration-line-through">
                {{ formatCurrency(item.requestedAmountCents) }}
              </span>
            </template>
            <template v-slot:item.fee="{ item }">
              <span :class="isBlikPaid(item) ? '' : 'grey--text'">
                {{ formatCurrency(item.feeCents) }}
              </span>
            </template>
            <template v-slot:item.totalDeducted="{ item }">
              <span v-if="isBlikPaid(item)">
                {{ formatCurrency(item.requestedAmountCents + item.feeCents) }}
              </span>
              <span v-else class="grey--text">—</span>
            </template>
            <template v-slot:item.status="{ item }">
              <v-chip x-small :color="blikStatusColor(item.status)" dark>
                {{ blikStatusLabel(item.status) }}
              </v-chip>
            </template>
            <template v-slot:item.createdAt="{ item }">
              {{ new Date(item.createdAt).toLocaleString('pl-PL') }}
            </template>
            <template v-slot:item.processedAt="{ item }">
              {{ item.processedAt ? new Date(item.processedAt).toLocaleString('pl-PL') : '—' }}
            </template>
            <template v-slot:item.provider="{ item }">
              {{ item.provider || '—' }}
              <div v-if="item.providerReference" class="caption grey--text">{{ item.providerReference }}</div>
            </template>
            <template v-slot:item.rejectionReason="{ item }">
              <span v-if="item.rejectionReason" :title="item.rejectionReason">
                {{ item.rejectionReason.length > 40 ? item.rejectionReason.slice(0,40) + '…' : item.rejectionReason }}
              </span>
              <span v-else-if="item.providerError" class="error--text" :title="item.providerError">
                ⚠ {{ item.providerError.length > 40 ? item.providerError.slice(0,40) + '…' : item.providerError }}
              </span>
              <span v-else>—</span>
            </template>
          </v-data-table>
        </v-tab-item>

        <!-- TAB 4: Wnioski podflot (do zatwierdzenia) -->
        <v-tab-item>
          <div class="filter-section mb-3">
            <div class="filter-section-header">
              <div class="filter-icon"><v-icon size="18" color="#5478FF">mdi-cash-minus</v-icon></div>
              <div class="filter-title">Wnioski o obciążenia kierowców (podfloty)</div>
              <button class="action-pill action-pill--neutral" @click="loadCharges" :disabled="chargesLoading">
                <v-icon size="14">mdi-refresh</v-icon>Odśwież
              </button>
            </div>
          </div>
          <v-data-table
            :headers="chargeHeaders"
            :items="charges"
            :items-per-page="50"
            class="elevation-1"
            dense
            no-data-text="Brak wniosków"
          >
            <template v-slot:[`item.createdAt`]="{ item }">
              {{ new Date(item.createdAt).toLocaleString('pl-PL') }}
            </template>
            <template v-slot:[`item.category`]="{ item }">{{ chargeCategoryLabel(item.category) }}</template>
            <template v-slot:[`item.amountCents`]="{ item }">
              <strong class="warning--text">{{ (item.amountCents / 100).toFixed(2) }} zł</strong>
            </template>
            <template v-slot:[`item.mileage`]="{ item }">
              {{ item.mileage ? item.mileage.toLocaleString('pl-PL') + ' km' : '—' }}
            </template>
            <template v-slot:[`item.attachmentName`]="{ item }">
              <v-chip
                v-if="item.attachmentFileId"
                x-small outlined link
                @click="downloadChargeAttachment(item.attachmentFileId)"
              >
                <v-icon x-small left>mdi-paperclip</v-icon>{{ item.attachmentName || 'plik' }}
              </v-chip>
              <span v-else class="grey--text">—</span>
            </template>
            <template v-slot:[`item.status`]="{ item }">
              <v-chip x-small :color="chargeStatusColor(item.status)" dark>
                {{ chargeStatusLabel(item.status) }}
              </v-chip>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <v-btn v-if="item.status === 'PENDING'" x-small color="success" class="mr-1" @click="approveCharge(item)">
                <v-icon x-small left>mdi-check</v-icon>Zatwierdź
              </v-btn>
              <v-btn v-if="item.status === 'PENDING'" x-small outlined color="error" @click="rejectCharge(item)">
                <v-icon x-small left>mdi-close</v-icon>Odrzuć
              </v-btn>
              <v-btn v-if="item.status === 'APPROVED'" x-small outlined color="warning" @click="unapproveCharge(item)" title="Cofnij zatwierdzenie">
                <v-icon x-small left>mdi-undo</v-icon>Cofnij
              </v-btn>
              <span v-if="item.status === 'REJECTED'" class="caption grey--text">—</span>
            </template>
          </v-data-table>
        </v-tab-item>
      </v-tabs-items>
  </div>
</template>
<script>
import * as dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import SignatureField from "@/components/SignatureField.vue";
dayjs.extend(isoWeek);
import Swal from "sweetalert2";
const axios = require("axios");
export default {
  components: { SignatureField },
  data() {
    return {
      activeTab: 0,
      loading: false,
      publishingAll: false,
      // Per-row blokada akcji — `{ [settlementId]: 'markPaid'|'publish'|'unpublish' }`.
      // Disabluje wszystkie row-actions tej linijki na czas requestu (anty double-click).
      rowBusy: {},
      search: "",
      options: {
        page: 1,
        itemsPerPage: 10,
      },
      totalItems: 0,
      // Invoice upload
      uploadInvoiceDialog: false,
      uploadInvoiceSettlementId: null,
      invoiceFile: null,
      // Invoice preview
      previewInvoiceDialog: false,
      previewUrl: null,
      previewIsPdf: false,
      previewLoading: false,
      // Filters
      filters: {
        subCompany: null,
        selectedWeek: dayjs().isoWeekYear() + '-W' + String(dayjs().isoWeek()).padStart(2, '0'),
        // "" = wszystkie schedule (WEEKLY/EVERY_2_WEEKS/MONTH/DAILY).
        // Wcześniej domyślnie WEEKLY ukrywał kierowców z innymi typami.
        settlementType: "",
      },
      // Table Headers
      headers: [
        { text: "Kierowca", value: "driver" },
        { text: "Typ Współpracy", value: "cooperationType" },
        { text: "Rozliczono?", value: "isSettled" },
        { text: "Typ wypłaty", value: "payoutType" },
        { text: "Zapłacono got.?", value: "isPaidCash" },
        { text: "Opublikowane?", value: "isPublished" },
        { text: "Faktura", value: "invoiceStatus" },
        { text: "Łącznie do wypłaty", value: "totalToPayout" },
        { text: "Brutto (z ZUS)", value: "grossWithZus" },
        // Kolumny Bonusy / Napiwki / Podatek 23% ukryte na życzenie (2026-07-14) —
        // dane dalej są w rozwinięciu wiersza.
        { text: "Przelew z tyt. zleceniówki", value: "netPayout" },
        { text: "Przelew z tyt. zwrotu kosztów", value: "costInvoiceReimbursement" },
        { text: "Przelew z tyt. najmu", value: "rentTransferAmount" },
        { text: "Komornik", value: "bailiffInfo", sortable: false },
        { text: "Akcje", value: "actions", sortable: false },
      ],
      subCompanies: [],
      settlementTypes: [
        { text: "Wszystkie", value: "" },
        { text: "Dziennie", value: "DAILY" },
        { text: "Tygodniowo", value: "WEEKLY" },
        { text: "Co 2 tygodnie", value: "EVERY_2_WEEKS" },
        { text: "Miesięcznie", value: "MONTH" },
      ],
      items: [],
      // Pool
      poolInfo: {
        poolTotal: 0,
        totalAllocated: 0,
        remaining: 0,
        invoiceCount: 0,
        allocations: [],
      },
      poolExpanded: false,
      allocationDialog: {
        show: false,
        settlementId: null,
        driverName: '',
        currentCostReimbursement: '',
        currentRentTransfer: '',
        amount: null,
      },
      // Transfer generation
      transferDialog: false,
      generatingTransfers: false,
      transferSummary: null,
      loadingSummary: false,
      // Expandable row breakdown (lazy-loaded per settlement)
      expandedRows: [],
      breakdownData: {},   // { [settlementId]: data }
      breakdownLoading: {}, // { [settlementId]: bool }
      bankAccounts: [],
      transferConfig: {
        bankFormat: 'MBANK',
        bankAccountId: null,
        senderAccountNo: '',
        senderName: '',
      },
      // Signature dialog for cash payment
      signatureDialog: {
        show: false,
        settlementId: null,
        // Wypłata kilku zaległych naraz — jeden podpis, jedno pokwitowanie.
        batchIds: null,
        // Zaległe wypłaty tego samego kierowcy, podpowiedziane przy pojedynczej
        // wypłacie — żeby nie trzeba było ich szukać po tabeli.
        related: [],
      },
      selectedRows: [],
      // Podpis na tablecie: obsługa nie podaje kierowcy komputera, tylko wysyła
      // prośbę na sparowane urządzenie leżące na ladzie.
      signatureDevices: [],
      tabletRequestId: null,
      tabletWaiting: false,
      tabletError: '',
      tabletPairing: null,
      tabletPoll: null,
      // Podpis przechwytywany na bieżąco (onEnd) — niezależny od resize/clear
      // pada przy submit. Źródło prawdy przy wysyłce pokwitowania.
      cashSig: { driver: null, worker: null },
      onDemandDialog: false,
      onDemandSending: false,
      onDemandLoadingDrivers: false,
      onDemandDrivers: [],
      onDemandDriverId: null,
      myCanUseFacsimile: false,
      myFacsimile: null,
      // Oddział wybierany przy pokwitowaniu (z dostępnych u operatora).
      cashSubCompanies: [],
      cashSubCompanyId: null,
      // Receipts tab
      receipts: {
        loading: false,
        search: '',
        items: [],
        total: 0,
        options: { page: 1, itemsPerPage: 10 },
        filters: {
          subCompany: null,
          selectedWeek: dayjs().isoWeekYear() + '-W' + String(dayjs().isoWeek()).padStart(2, '0'),
          settlementType: 'WEEKLY',
        },
        headers: [
          { text: 'Kierowca', value: 'driverName' },
          { text: 'Typ współpracy', value: 'cooperationType' },
          { text: 'Kwota', value: 'totalToPayout' },
          { text: 'Data rozliczenia', value: 'date' },
          { text: 'Data wypłaty', value: 'updatedAt' },
          { text: 'Podpisy', value: 'signatures', sortable: false },
          { text: 'Akcje', value: 'actions', sortable: false },
        ],
      },
      // BLIK self-service history tab
      blik: {
        loading: false,
        items: [],
        filters: {
          from: dayjs().startOf('month').format('YYYY-MM-DD'),
          to: dayjs().format('YYYY-MM-DD'),
          status: null,
          search: '',
        },
        headers: [
          { text: 'Kierowca', value: 'driver', sortable: false },
          { text: 'Telefon', value: 'phone', sortable: false },
          { text: 'Wypłacono', value: 'requestedAmount', align: 'end' },
          { text: 'Opłata', value: 'fee', align: 'end' },
          { text: 'Razem z opłatą', value: 'totalDeducted', align: 'end' },
          { text: 'Status', value: 'status' },
          { text: 'Zgłoszono', value: 'createdAt' },
          { text: 'Zrealizowano', value: 'processedAt' },
          { text: 'Provider', value: 'provider', sortable: false },
          { text: 'Uwagi', value: 'rejectionReason', sortable: false },
        ],
      },
      // SubFleet charges
      charges: [],
      chargesLoading: false,
      chargeHeaders: [
        { text: 'Data zgłoszenia', value: 'createdAt' },
        { text: 'Podflota / Auto', value: 'subFleetCar', sortable: false },
        { text: 'Zgłosił', value: 'requestedByName', sortable: false },
        { text: 'Kierowca', value: 'targetDriverName', sortable: false },
        { text: 'Kategoria', value: 'category' },
        { text: 'Kwota', value: 'amountCents', align: 'end' },
        { text: 'Przebieg', value: 'mileage', align: 'end' },
        { text: 'Opis', value: 'description', sortable: false },
        { text: 'Załącznik', value: 'attachmentName', sortable: false },
        { text: 'Status', value: 'status' },
        { text: 'Akcje', value: 'actions', sortable: false, align: 'end' },
      ],
    };
  },
  watch: {
    options: {
      handler() {
        this.initialize();
      },
      deep: true,
    },
    search: {
      handler() {
        this.initialize();
      },
      deep: true,
    },
    // Zmiana tygodnia ma od razu przeładować dane (a więc i stan przycisku
    // „Opublikuj"). Wcześniej zależało to od @change na input[type=week],
    // które przy wyborze z pickera odpala się dopiero po blur — stąd
    // wrażenie, że trzeba odświeżyć stronę.
    'filters.selectedWeek': {
      handler() {
        this.initialize();
      },
    },
    activeTab(val) {
      if (val === 1) {
        this.fetchReceipts();
      } else if (val === 2) {
        this.fetchBlik();
      } else if (val === 3) {
        this.loadCharges();
      }
    },
    'receipts.options': {
      handler() {
        if (this.activeTab === 1) this.fetchReceipts();
      },
      deep: true,
    },
  },
  computed: {
    /**
     * Podpowiedź pod nazwą nadawcy. Bank przyjmuje w tym polu linie po
     * 35 znaków (mBank: dwie, czyli 70 razem). Dłuższa nazwa dzieli się
     * w środku słowa, a jeszcze dłuższa zostanie przycięta — biuro ma to
     * wiedzieć PRZED wysłaniem pliku, a nie dowiedzieć się z odrzucenia.
     */
    podpowiedzNadawcy() {
      const dl = (this.transferConfig.senderName || '').length;
      if (dl === 0) return 'Wpisz nazwę nadawcy — bez niej nie zbudujemy paczki.';
      if (dl > 70) return `${dl} znaków — bank przyjmie tylko 70, reszta zostanie obcięta. Skróć nazwę.`;
      if (dl > 35) return `${dl} znaków — bank podzieli nazwę na dwie linie. Zmieści się, ale krócej wygląda lepiej na wyciągu.`;
      return `${dl} z 35 znaków — zmieści się w jednej linii.`;
    },
    // Do wypłaty zbiorczej wchodzą wyłącznie gotówkowe, jeszcze niewypłacone
    // rozliczenia JEDNEGO kierowcy — pokwitowanie podpisuje jedna osoba,
    // więc nie może obejmować cudzych wypłat.
    cashBatchCandidates() {
      return (this.selectedRows || []).filter(
        (r) => r.payoutTypeRaw === 'CASH' && !r.isSettledRaw,
      );
    },
    cashBatchDrivers() {
      return [...new Set(this.cashBatchCandidates.map((r) => r.driverId || r.driverName))];
    },
    cashBatchReady() {
      return (
        this.cashBatchCandidates.length > 1 &&
        this.cashBatchCandidates.length === this.selectedRows.length &&
        this.cashBatchDrivers.length === 1
      );
    },
    cashBatchTotal() {
      return this.cashBatchCandidates.reduce((sum, r) => sum + (r.kwotaDoRekiRaw || 0), 0);
    },
    // Kwota, którą kierowca faktycznie podpisuje: bieżące rozliczenie plus
    // zaznaczone zaległe.
    cashDialogTotal() {
      const current = (this.items || []).find(
        (i) => i.id === this.signatureDialog.settlementId,
      );
      const base = current ? current.kwotaDoRekiRaw || 0 : 0;
      return (this.signatureDialog.related || [])
        .filter((r) => r.checked)
        .reduce((sum, r) => sum + (r.amount || 0), base);
    },
    // Rozliczenia, które wchodzą do tej wypłaty (pojedyncza + zaznaczone
    // zaległe albo zaznaczenie zbiorcze z tabeli).
    cashDialogRows() {
      const ids = this.signatureDialog.batchIds || [
        this.signatureDialog.settlementId,
        ...(this.signatureDialog.related || []).filter((r) => r.checked).map((r) => r.id),
      ];
      return (this.items || []).filter((i) => ids.includes(i.id));
    },
    cashDialogDoReki() {
      return this.cashDialogRows.reduce((sum, r) => sum + (r.kwotaDoRekiRaw || 0), 0);
    },
    // Część, która idzie przelewem do komornika — nie wydajemy jej z kasy.
    cashDialogKomornik() {
      return this.cashDialogRows.reduce(
        (sum, r) => sum + ((r.totalToPayoutRaw || 0) - (r.kwotaDoRekiRaw || 0)),
        0,
      );
    },
    cashBatchHint() {
      if (!this.selectedRows.length) return '';
      if (this.cashBatchCandidates.length !== this.selectedRows.length) {
        return 'Zaznaczone są pozycje, których nie da się wypłacić gotówką (już wypłacone albo przelewowe)';
      }
      if (this.cashBatchDrivers.length > 1) {
        return 'Jedno pokwitowanie może dotyczyć tylko jednego kierowcy';
      }
      if (this.cashBatchCandidates.length < 2) {
        return 'Zaznacz co najmniej dwa rozliczenia — pojedyncze wypłacisz przyciskiem w wierszu';
      }
      return '';
    },
    pendingCharges() {
      return (this.charges || []).filter(c => c.status === 'PENDING');
    },
    // Czy jest cokolwiek do opublikowania w aktualnie wczytanym tygodniu.
    // Liczy się z this.items, więc po zmianie tygodnia (initialize przeładowuje
    // listę) przelicza się sam — przycisk wyszarza się gdy wszystko opublikowane.
    hasUnpublished() {
      return (this.items || []).some((it) => !it.isPublishedRaw);
    },
    // Schedule path-segment dla bulk endpointów (publish/unpublish/finalize
    // /revert/mark-paid -all). Pusty filtr ("Wszystkie") wcześniej tworzył
    // URL z pustym segmentem, NestJS rzucał 404. Backend rozumie 'ALL' jako
    // brak filtra.
    scheduleSegment() {
      return this.filters.settlementType || 'ALL';
    },
    totalTransferCount() {
      const c = this.transferSummary && this.transferSummary.counts;
      if (!c) return 0;
      return (c.zleceniowka || 0) + (c.zwrot_kosztow || 0) + (c.najem || 0) + (c.komornik || 0) + (c.zaliczki_dzienne || 0);
    },
    totalCashCount() {
      const c = this.transferSummary && this.transferSummary.cash && this.transferSummary.cash.counts;
      if (!c) return 0;
      return (c.zleceniowka || 0) + (c.zwrot_kosztow || 0) + (c.najem || 0) + (c.zaliczki_dzienne || 0);
    },
    blikStatusOptions() {
      return [
        { text: 'Wszystkie', value: null },
        { text: 'Zrealizowane', value: 'PAID' },
        { text: 'Zatwierdzone', value: 'APPROVED' },
        { text: 'Auto-zatwierdzone', value: 'AUTO_APPROVED' },
        { text: 'W trakcie', value: 'PROCESSING' },
        { text: 'Błąd', value: 'FAILED' },
        { text: 'Odrzucone', value: 'REJECTED' },
      ];
    },
    blikFiltered() {
      const q = (this.blik.filters.search || '').trim().toLowerCase();
      let items = this.blik.items || [];
      if (this.blik.filters.status) {
        items = items.filter(i => i.status === this.blik.filters.status);
      }
      if (q) {
        items = items.filter(i => {
          const name = i.driver ? (i.driver.firstName + ' ' + i.driver.lastName).toLowerCase() : '';
          const phone = (i.driver && i.driver.phone) || '';
          return name.includes(q) || phone.includes(q);
        });
      }
      return items;
    },
    blikSummary() {
      const list = this.blikFiltered;
      const paidStatuses = new Set(['PAID', 'APPROVED', 'AUTO_APPROVED', 'PROCESSING']);
      let totalAmount = 0;   // faktycznie wypłacone kierowcom (netto)
      let totalFee = 0;      // opłaty zebrane przez BTP (tylko od wypłaconych)
      let totalDeducted = 0; // razem zdjęte z salda kierowców
      let paid = 0;
      let rejected = 0;
      for (const r of list) {
        if (paidStatuses.has(r.status)) {
          totalAmount += r.requestedAmountCents || 0;
          totalFee += r.feeCents || 0;
          totalDeducted += (r.requestedAmountCents || 0) + (r.feeCents || 0);
          if (r.status === 'PAID') paid++;
        }
        if (r.status === 'REJECTED' || r.status === 'FAILED') rejected++;
      }
      return { count: list.length, totalAmount, totalFee, totalDeducted, paid, rejected };
    },
  },
  async created() {
    await this.getSubcompanySelect();
    await this.fetchBankAccounts();
    await this.fetchCompanyInfo();
    await this.fetchMyFacsimile();
    await this.initialize();
  },
  methods: {
    // Im dłużej rozliczenie wisi, tym mocniej to widać — 22 dni bez oznaczenia
    // kosztowały w sierpniu 2026 trzy zbędne przelewy do komornika.
    lateDaysClass(days, isCash) {
      // Gotówkowe czekają na odbiór przez kierowcę — to normalny bieg rzeczy,
      // więc nie straszymy kolorem. Ale miesiąc bez odbioru to już sygnał, że
      // ktoś po pieniądze nie przyszedł i warto zapytać dlaczego.
      if (isCash) {
        return days >= 30
          ? 'orange--text text--lighten-1 font-weight-medium'
          : 'grey--text';
      }
      if (days >= 14) return 'red--text text--lighten-1 font-weight-bold';
      if (days >= 7) return 'orange--text text--lighten-1 font-weight-medium';
      return 'grey--text';
    },
    async fetchMyFacsimile() {
      try {
        const me = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "company-admins/me",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        this.myCanUseFacsimile = !!me.data.data.canUseFacsimile;
        this.myFacsimile = me.data.data.facsimileSignature || null;
      } catch (e) { /* faksymilka opcjonalna */ }
    },
    useFacsimileForWorker() {
      if (this.myFacsimile) {
        this.cashSig.worker = this.myFacsimile;
        try { this.$refs.cashWorkerSignaturePad?.clearSignature(); } catch (e) { /* ignore */ }
      }
    },
    // Cały wiersz na zielono gdy kierowca jest wypłacony/rozliczony
    // (przelew zatwierdzony lub gotówka z pokwitowaniem).
    rowClass(item) {
      return item && (item.isSettledRaw || item.isPaidCashRaw)
        ? 'payouts-paid-row'
        : '';
    },
    async loadCharges() {
      this.chargesLoading = true;
      try {
        const res = await axios.get(
          process.env.VUE_APP_ROOT_API + 'sub-fleet/charges',
          { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } },
        );
        this.charges = res.data?.data || [];
      } catch (e) {
        this.charges = [];
      }
      this.chargesLoading = false;
    },
    async approveCharge(item) {
      const ok = await Swal.fire({
        icon: 'question', title: 'Zatwierdzić obciążenie?',
        html: `<strong>${item.targetDriverName}</strong><br>${this.chargeCategoryLabel(item.category)} — ${(item.amountCents/100).toFixed(2)} zł<br><small>Po zatwierdzeniu zostanie utworzony Bonus typu NEGATIVE na kierowcy.</small>`,
        showCancelButton: true, confirmButtonText: 'Tak, zatwierdź', cancelButtonText: 'Anuluj',
      });
      if (!ok.isConfirmed) return;
      try {
        await axios.post(
          process.env.VUE_APP_ROOT_API + 'sub-fleet/charges/' + item.id + '/approve',
          {},
          { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } },
        );
        await this.loadCharges();
        Swal.fire({ icon: 'success', title: 'Zatwierdzone', timer: 1500, showConfirmButton: false });
      } catch (e) {
        Swal.fire('Błąd', e.response?.data?.message || 'Nie udało się zatwierdzić', 'error');
      }
    },
    async unapproveCharge(item) {
      const ok = await Swal.fire({
        icon: 'warning', title: 'Cofnąć zatwierdzenie?',
        html: `<strong>${item.targetDriverName}</strong><br>${this.chargeCategoryLabel(item.category)} — ${(item.amountCents/100).toFixed(2)} zł<br><small class="error--text">Bonus zostanie usunięty (jeśli nie jest jeszcze w rozliczeniu). Wniosek wróci do oczekujących.</small>`,
        showCancelButton: true, confirmButtonText: 'Tak, cofnij', cancelButtonText: 'Anuluj',
      });
      if (!ok.isConfirmed) return;
      try {
        await axios.post(
          process.env.VUE_APP_ROOT_API + 'sub-fleet/charges/' + item.id + '/unapprove',
          {},
          { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } },
        );
        await this.loadCharges();
        Swal.fire({ icon: 'success', title: 'Cofnięte', timer: 1500, showConfirmButton: false });
      } catch (e) {
        Swal.fire('Błąd', e.response?.data?.message || 'Nie udało się cofnąć', 'error');
      }
    },
    async rejectCharge(item) {
      const r = await Swal.fire({
        icon: 'warning', title: 'Powód odrzucenia',
        input: 'textarea', inputPlaceholder: 'Krótki powód (opcjonalnie)',
        showCancelButton: true, confirmButtonText: 'Odrzuć', cancelButtonText: 'Anuluj',
      });
      if (!r.isConfirmed) return;
      try {
        await axios.post(
          process.env.VUE_APP_ROOT_API + 'sub-fleet/charges/' + item.id + '/reject',
          { reason: r.value || '' },
          { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } },
        );
        await this.loadCharges();
      } catch (e) {
        Swal.fire('Błąd', e.response?.data?.message || 'Nie udało się odrzucić', 'error');
      }
    },
    async downloadChargeAttachment(fileId) {
      if (!fileId) return;
      try {
        const res = await axios.post(
          process.env.VUE_APP_ROOT_API + 'aws/download-file/' + fileId,
          {},
          {
            headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
            responseType: 'blob',
          },
        );
        const blob = new Blob([res.data], { type: res.data.type });
        const url = window.URL.createObjectURL(blob);
        window.open(url);
      } catch (e) {
        Swal.fire('Błąd', 'Nie udało się pobrać pliku', 'error');
      }
    },
    chargeCategoryLabel(cat) {
      return ({
        OIL: 'Serwis olejowy', TIRES: 'Opony', REPAIR: 'Naprawa',
        FINE: 'Mandat', WASH: 'Mycie', OTHER: 'Inne',
      })[cat] || cat;
    },
    chargeStatusColor(s) {
      if (s === 'PENDING') return 'orange';
      if (s === 'APPROVED') return 'success';
      if (s === 'REJECTED') return 'error';
      return 'grey';
    },
    chargeStatusLabel(s) {
      if (s === 'PENDING') return 'Oczekuje';
      if (s === 'APPROVED') return 'Zaakceptowany';
      if (s === 'REJECTED') return 'Odrzucony';
      return s;
    },
    async fetchBlik() {
      this.blik.loading = true;
      try {
        const params = new URLSearchParams();
        if (this.blik.filters.from) params.set('from', this.blik.filters.from);
        if (this.blik.filters.to) params.set('to', this.blik.filters.to);
        const res = await axios({
          method: 'get',
          url: process.env.VUE_APP_ROOT_API + 'self-service/admin/history?' + params.toString(),
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        this.blik.items = (res.data && res.data.data) || [];
      } catch (e) {
        console.error('Error fetching BLIK history:', e);
        const msg = e.response?.data?.message || 'Nie udało się pobrać historii BLIK';
        Swal.fire('Błąd', msg.toString(), 'error');
      }
      this.blik.loading = false;
    },
    isBlikPaid(item) {
      // Status oznaczający, że pieniądze faktycznie poszły (lub są w drodze) do kierowcy
      return ['PAID', 'APPROVED', 'AUTO_APPROVED', 'PROCESSING'].includes(item.status);
    },
    blikStatusColor(s) {
      switch (s) {
        case 'PAID': return 'success';
        case 'APPROVED':
        case 'AUTO_APPROVED': return 'info';
        case 'PROCESSING': return 'primary';
        case 'FAILED': return 'error';
        case 'REJECTED': return 'warning';
        default: return 'grey';
      }
    },
    blikStatusLabel(s) {
      switch (s) {
        case 'PAID': return 'Zrealizowane';
        case 'APPROVED': return 'Zatwierdzone';
        case 'AUTO_APPROVED': return 'Auto-OK';
        case 'PROCESSING': return 'W trakcie';
        case 'FAILED': return 'Błąd';
        case 'REJECTED': return 'Odrzucone';
        case 'REQUESTED': return 'Zgłoszone';
        case 'NEEDS_APPROVAL': return 'Do akceptacji';
        default: return s;
      }
    },
    async toggleLoading(type) {
      const toggle = type;
      localStorage.setItem("loading", toggle);
      window.dispatchEvent(
        new CustomEvent("toggleLoading", {
          detail: {
            loading: localStorage.getItem("loading"),
            overlay: localStorage.getItem("loading"),
          },
        })
      );
    },
    async fetchCompanyInfo() {
      try {
        const res = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "company",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        const companies = res.data.data;
        // Wybierz firmę aktualnie aktywną (przełączaną z górnej belki przez
        // /admin-auth/switch-company). Bez tego super-admin z dostępem do wielu
        // firm dostawał zawsze pierwszą z listy → koszyki generowały się z
        // danymi firmy nadawcy z innej spółki niż widoczna na ekranie.
        const activeId = localStorage.getItem("activeCompanyId");
        let company = null;
        if (Array.isArray(companies)) {
          if (activeId) company = companies.find(c => c.id === activeId);
          if (!company) company = companies[0];
        } else {
          company = companies;
        }
        if (company) {
          // SAMA NAZWA FIRMY, bez adresu.
          //
          // Do 05.10.2026 sklejało się tu `nazwa + ulica + kod + miasto`, co dla
          // BTP dawało „BTP sp. z o.o. Makowa 85-361 Bydgoszcz" — 38 znaków.
          // mBank przyjmuje w tym polu **35 znaków na linię** i odrzucił całą
          // paczkę komorniczą na tydzień 40: rekord 001, pole 08 („nazwa i adres
          // zleceniodawcy 1"), „pole przekracza dopuszczalną długość".
          // Błąd wychodził na pierwszym rekordzie, bo nadawca jest ten sam
          // w każdym — jeden za długi napis blokował przelewy całej paczki.
          //
          // Adresu bank nie potrzebuje: płatnika rozpoznaje po numerze rachunku.
          // Backend i tak dzieli te pola po 35 znaków (`common/bank/elixir.ts`),
          // ale lepiej wysłać czystą nazwę niż nazwę przełamaną w środku słowa.
          //
          // Ręczna zmiana w polu jest pamiętana — biuro nie musi jej powtarzać.
          const zapamietana = localStorage.getItem('payoutBasketSender');
          this.transferConfig.senderName = zapamietana || company.name || '';
        }
      } catch (e) {
        console.error("Failed to fetch company info:", e);
      }
    },
    async onRowExpanded({ item, value }) {
      if (!value || !item?.id) return;
      if (this.breakdownData[item.id]) return; // już mamy w cache
      this.$set(this.breakdownLoading, item.id, true);
      try {
        const res = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + `settlements/${item.id}/breakdown`,
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        this.$set(this.breakdownData, item.id, res.data.data);
      } catch (e) {
        console.error("Failed to load breakdown:", e);
        this.$set(this.breakdownData, item.id, null);
      }
      this.$set(this.breakdownLoading, item.id, false);
    },
    // Recompute rent transfer ze świeżych pól, bo stored rentTransferAmount
    // u części starych rekordów (Michał) jest buggy — zawiera ZUS w środku.
    // Zamiast wyświetlać zwalony stored, liczymy: pozostało − zwrot kosztów.
    rentTransferFor(itemId) {
      const b = this.breakdownData?.[itemId];
      if (!b || !b.settlement) return 0;
      const isNet = !!b.settlement.totalToPayoutIsNet;
      const total = b.settlement.totalToPayout || 0;
      const zus = b.settlement.zusDeduction || 0;
      const afterZus = isNet ? total : total - zus;
      const zleceniowka = b.settlement.zleceniowkaAmount || 0;
      const costInvoice = b.settlement.costInvoiceReimbursement || 0;
      return Math.max(0, afterZus - zleceniowka - costInvoice);
    },
    platformLabel(p) {
      return ({ BOLT: 'Bolt', UBER: 'Uber', FREENOW: 'Free Now' })[p] || p;
    },
    formatGroszy(g) {
      const n = Number(g || 0) / 100;
      return n.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' zł';
    },
    isBonusDeduction(b) {
      // Bonus typu NEGATIVE (kara/potrącenie) ma dodatnią cenę, ale jest ODEJMOWANY
      // od wypłaty — pokazujemy go z minusem na czerwono. Tak samo cena ujemna.
      return (b && b.type === 'NEGATIVE') || Number(b?.price) < 0;
    },
    async fetchBankAccounts() {
      try {
        const res = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "bank-accounts",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        this.bankAccounts = res.data.data.map(ba => ({
          id: ba.id,
          accountNo: ba.bankAccountNumber,
          bankName: ba.bankName,
          label: `${ba.bankName} - ${ba.bankAccountNumber}`,
        }));
      } catch (e) {
        console.error("Failed to fetch bank accounts:", e);
      }
    },
    onBankAccountSelect(id) {
      const ba = this.bankAccounts.find(b => b.id === id);
      if (ba) {
        this.transferConfig.senderAccountNo = ba.accountNo;
      }
    },
    async getSubcompanySelect() {
      await this.toggleLoading(true);
      try {
        const subCompanies = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + `sub-company`,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        this.subCompanies = subCompanies.data.data.map((it) => {
          return {
            id: it.id,
            name: it.name,
          };
        });
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response.data.message.toString(), "error");
      }
      await this.toggleLoading(false);
    },
    getWeekDates() {
      // Convert week string (e.g., "2024-W01") to start and end dates
      const [yearStr, weekStr] = this.filters.selectedWeek.split('-');
      const week = parseInt(weekStr.replace('W', ''));
      const year = parseInt(yearStr);

      // Find the first day of the year (Jan 4 is always in week 1 ISO)
      const jan4 = dayjs(`${year}-01-04`);
      const dayOfWeek = jan4.day();
      const firstMonday = jan4.subtract((dayOfWeek + 6) % 7, 'day');
      const start = firstMonday.add((week - 1) * 7, 'day');
      const end = start.add(6, 'day');

      return {
        startDate: start.format('YYYY-MM-DD'),
        endDate: end.format('YYYY-MM-DD'),
      };
    },
    getPoolMonthYear() {
      const { endDate } = this.getWeekDates();
      // Use the end date's month as the pool month (most of the week typically falls in this month)
      const end = dayjs(endDate);
      return {
        month: end.month() + 1,
        year: end.year(),
      };
    },
    formatCurrency(value) {
      return (value / 100).toFixed(2) + ' PLN';
    },
    customSort(items, sortBy, sortDesc) {
      if (!sortBy || sortBy.length === 0) return items;
      const key = sortBy[0];
      const desc = sortDesc[0];
      const rawMap = {
        cooperationType: 'cooperationTypeRaw',
        isSettled: 'isSettledRaw',
        payoutType: 'payoutTypeRaw',
        isPaidCash: 'isPaidCashRaw',
        isPublished: 'isPublishedRaw',
        totalToPayout: 'totalToPayoutRaw',
        grossWithZus: 'grossWithZusRaw',
        bonusAmount: 'bonusAmountRaw',
        tipAmount: 'tipAmountRaw',
        taxAmount: 'taxAmountRaw',
        netPayout: 'netPayoutRaw',
        costInvoiceReimbursement: 'costInvoiceReimbursementRaw',
        rentTransferAmount: 'rentTransferAmountRaw',
        invoiceStatus: 'invoiceStatusRaw',
      };
      const sortKey = rawMap[key] || key;
      const sorted = [...items].sort((a, b) => {
        const av = a[sortKey];
        const bv = b[sortKey];
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        if (typeof av === 'number' && typeof bv === 'number') return av - bv;
        if (typeof av === 'boolean' && typeof bv === 'boolean') return (av === bv ? 0 : av ? 1 : -1);
        return String(av).localeCompare(String(bv), 'pl');
      });
      return desc ? sorted.reverse() : sorted;
    },
    formatCooperationType(type) {
      const labels = {
        'B2B_VAT': 'B2B VAT',
        'B2B_NO_VAT': 'B2B bez VAT',
        'FIFTY_FIFTY': 'Zlecenie 50/50',
        'RENT': 'Najem',
        'CONNECTION': 'Podpięcie',
        'STUDENT': 'Student',
      };
      return labels[type] || type || '-';
    },
    isEligibleForPool(item) {
      const eligible = ['CONNECTION', 'RENT', 'FIFTY_FIFTY', 'STUDENT'];
      return eligible.includes(item.cooperationTypeRaw);
    },
    async fetchPoolInfo() {
      try {
        const { month, year } = this.getPoolMonthYear();
        const response = await axios.get(
          process.env.VUE_APP_ROOT_API + `cost-invoice-pool/summary`,
          {
            params: { month, year },
            headers: {
              Authorization: "Bearer " + localStorage.getItem("token"),
            },
          }
        );
        this.poolInfo = response.data.data;
      } catch (e) {
        console.log(e);
        this.poolInfo = { poolTotal: 0, totalAllocated: 0, remaining: 0, invoiceCount: 0, allocations: [] };
      }
    },
    async fetchTransferSummary() {
      this.loadingSummary = true;
      try {
        const { startDate, endDate } = this.getWeekDates();
        const res = await axios.get(process.env.VUE_APP_ROOT_API + "settlements/transfer-summary", {
          params: { startDate, endDate, payoutSchedule: this.filters.settlementType || undefined },
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        this.transferSummary = (res.data && res.data.data) || null;
      } catch (e) {
        this.transferSummary = null;
      }
      this.loadingSummary = false;
    },
    async initialize() {
      this.loading = true;
      this.fetchTransferSummary();
      if (this.search.trim().length > 0) {
        this.options.page = 1;
      }
      try {
        const { startDate, endDate } = this.getWeekDates();
        const params = {
          startDate,
          endDate,
          payoutSchedule: this.filters.settlementType,
        };
        // Add subCompanyIds as array if selected
        if (this.filters.subCompany && this.filters.subCompany.length > 0) {
          params.subCompanyIds = this.filters.subCompany;
        }
        const response = await axios.get(
          process.env.VUE_APP_ROOT_API +
            `settlements/payouts?take=10000&skip=1&search=${this.search}`,
          {
            params,
            paramsSerializer: (params) => {
              const searchParams = new URLSearchParams();
              for (const key of Object.keys(params)) {
                const value = params[key];
                if (Array.isArray(value)) {
                  value.forEach((v) => searchParams.append(key, v));
                } else if (value !== undefined && value !== null) {
                  searchParams.append(key, value);
                }
              }
              return searchParams.toString();
            },
            headers: {
              Authorization: "Bearer " + localStorage.getItem("token"),
            },
          }
        );
        this.items = response.data.data.result.map((it) => {
          const bonusRaw = (it.bonuses || []).filter(b => b.type === 'POSITIVE').reduce((sum, b) => sum + b.price, 0);
          const tipRaw = (it.incomes || []).reduce((sum, inc) => sum + (inc.netto || 0), 0);
          const taxRaw = Math.round((bonusRaw + tipRaw) * 0.23);
          // totalToPayout jest już po ZUS-ie (od commit'a 'Change totalToPayout
          // to be net'). Brutto pokazujemy oddzielnie jako totalToPayout +
          // zusDeduction. Dla starych rekordów (sprzed migracji) wartość
          // może być nadal brutto — admin powinien je cofnąć i wygenerować
          // ponownie.
          const grossWithZus = (it.totalToPayout || 0) + (it.zusDeduction || 0);
          return {
            id: it.id,
            driver: it.driver.firstName + " " + it.driver.lastName,
            driverId: it.driver?.id,
            cooperationType: this.formatCooperationType(it.cooperationType),
            cooperationTypeRaw: it.cooperationType,
            isSettled: it.isSettled ? "Tak" : "Nie",
            isSettledRaw: it.isSettled,
            payoutType: it.driver?.payoutType === 'CASH' ? 'Gotówka' : 'Przelew',
            payoutTypeRaw: it.driver?.payoutType,
            isPaidCash: it.isPaidCash ? "Tak" : "Nie",
            isPaidCashRaw: it.isPaidCash,
            isPublished: it.isPublished ? "Tak" : "Nie",
            isPublishedRaw: it.isPublished,
            isInvoiceUploaded: it.isInvoiceUploaded,
            invoiceFileId: it.invoiceFileId,
            invoiceStatusB2B: it.invoiceStatus || null,
            invoiceRejectReason: it.invoiceRejectReason || null,
            isB2B: it.cooperationType === 'B2B_VAT' || it.cooperationType === 'B2B_NO_VAT',
            invoiceStatusRaw: (it.cooperationType === 'B2B_VAT' || it.cooperationType === 'B2B_NO_VAT')
              ? (it.invoiceStatus === 'PENDING' ? 0 : it.isInvoiceUploaded ? 1 : 2)
              : 3,
            totalToPayout: this.formatCurrency(it.totalToPayout),
            totalToPayoutRaw: it.totalToPayout || 0,
            // Do ręki = po odjęciu części komorniczej (liczy serwer, tak samo
            // jak pokwitowanie i wpis kasowy).
            kwotaDoRekiRaw: it.kwotaDoReki != null ? it.kwotaDoReki : it.totalToPayout || 0,
            settlementDate: it.date,
            grossWithZus: this.formatCurrency(grossWithZus),
            grossWithZusRaw: grossWithZus,
            bonusAmountRaw: bonusRaw,
            tipAmountRaw: tipRaw,
            bonusAmount: this.formatCurrency(bonusRaw),
            tipAmount: this.formatCurrency(tipRaw),
            taxAmount: this.formatCurrency(taxRaw),
            taxAmountRaw: taxRaw,
            netPayout: this.formatCurrency(it.netPayout || 0),
            netPayoutRaw: it.netPayout || 0,
            costInvoiceReimbursement: this.formatCurrency(it.costInvoiceReimbursement || 0),
            costInvoiceReimbursementRaw: it.costInvoiceReimbursement || 0,
            rentTransferAmount: this.formatCurrency(it.rentTransferAmount || 0),
            rentTransferAmountRaw: it.rentTransferAmount || 0,
            bailiffName: it.driver?.bailiffs?.[0]?.name || null,
            bailiffTypes: it.driver?.bailiffs?.[0]?.transferTypes?.join(', ') || '',
          };
        });
        this.totalItems = response.data.data.count;
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response.data.message.toString(), "error");
      }
      // Po zwykłych kierowcach — dorzuć wiersze właścicieli podflot za ten sam tydzień
      try {
        await this.appendSubFleetPayouts();
      } catch (e) {
        console.error('Failed to load subfleet payouts:', e);
      }
      this.loading = false;
      await this.fetchPoolInfo();
    },

    async appendSubFleetPayouts() {
      // selectedWeek jest w formacie "2026-W17" (ISO week z <input type="week">)
      const wk = this.filters.selectedWeek || '';
      const m = wk.match(/^(\d{4})-W(\d{1,2})$/);
      if (!m) return;
      const year = parseInt(m[1], 10);
      const week = parseInt(m[2], 10);
      const res = await axios.get(
        process.env.VUE_APP_ROOT_API + `sub-fleet/payouts/weekly?year=${year}&week=${week}`,
        { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } },
      );
      const list = res.data?.data || [];
      // Wypłata podfloty siedzi już w ZWYKŁYM rozliczeniu właściciela (bonus
      // „Wypłata podfloty …”). Osobny wiersz liczony na bieżąco dublował ją
      // i miał złe kolumny (cały czynsz jako „do wypłaty”, prowizja jako
      // „najem”) — zamiast niego oznaczamy prawdziwy wiersz właściciela.
      for (const sf of list) {
        if (!sf.ownerDriverId) continue;
        for (const it of this.items) {
          if (it.driverId !== sf.ownerDriverId) continue;
          this.$set(it, 'isSubFleetOwner', true);
          this.$set(it, 'subFleetName', sf.subFleetName);
          this.$set(it, 'carCount', sf.carCount);
        }
      }
    },

    goToDriver(id) {
      if (id) location.href = '/driver-details?id=' + id;
    },

    openAllocationDialog(item) {
      this.allocationDialog = {
        show: true,
        settlementId: item.id,
        driverName: item.driver,
        currentCostReimbursement: item.costInvoiceReimbursement,
        currentRentTransfer: item.rentTransferAmount,
        amount: null,
      };
    },

    async submitAllocation() {
      const amountPln = parseFloat(this.allocationDialog.amount);
      if (!amountPln || amountPln <= 0) {
        Swal.fire("Błąd", "Podaj prawidłową kwotę", "error");
        return;
      }
      const amountGrosze = Math.round(amountPln * 100);
      if (amountGrosze > this.poolInfo.remaining) {
        Swal.fire("Błąd", "Kwota przekracza dostępną pulę", "error");
        return;
      }

      const result = await Swal.fire({
        title: "Potwierdzenie alokacji",
        text: `Przydzielić ${amountPln.toFixed(2)} PLN z puli kosztowej dla ${this.allocationDialog.driverName}?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Tak, przydziel",
        cancelButtonText: "Anuluj",
      });

      if (result.isConfirmed) {
        try {
          const { month, year } = this.getPoolMonthYear();
          await axios({
            method: "post",
            url: process.env.VUE_APP_ROOT_API + "cost-invoice-pool/allocate",
            data: {
              settlementId: this.allocationDialog.settlementId,
              amount: amountGrosze,
              month,
              year,
            },
            headers: {
              Authorization: "Bearer " + localStorage.getItem("token"),
            },
          });
          this.allocationDialog.show = false;
          Swal.fire("Sukces", "Kwota została przydzielona", "success");
          await this.initialize();
        } catch (e) {
          console.log(e);
          Swal.fire("Błąd", e.response?.data?.message?.toString() || "Wystąpił błąd", "error");
        }
      }
    },

    async revertPoolAllocation(id) {
      const result = await Swal.fire({
        title: "Cofnąć alokację z puli?",
        text: "Kwota wróci do puli kosztowej",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Tak, cofnij",
        cancelButtonText: "Anuluj",
      });

      if (result.isConfirmed) {
        try {
          await axios({
            method: "post",
            url: process.env.VUE_APP_ROOT_API + "cost-invoice-pool/revert/" + id,
            headers: {
              Authorization: "Bearer " + localStorage.getItem("token"),
            },
          });
          Swal.fire("Sukces", "Alokacja cofnięta", "success");
          await this.initialize();
        } catch (e) {
          console.log(e);
          Swal.fire("Błąd", e.response?.data?.message?.toString() || "Wystąpił błąd", "error");
        }
      }
    },

    async revertPayout(id) {
      Swal.fire({
        title: "Napewno cofnać?",
        text: "",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Tak",
        cancelButtonText: "Anuluj",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await axios({
              method: "post",
              url: process.env.VUE_APP_ROOT_API + "settlements/revert/" + id,
              headers: {
                Authorization: "Bearer " + localStorage.getItem("token"),
              },
            });
            await this.initialize();
          } catch (e) {
            console.log(e);
            Swal.fire("Błąd", e.response.data.message.toString(), "error");
          }
        }
      });
    },
    async revertAll() {
      Swal.fire({
        title: "Napewno cofnać wszystkie rozliczenia?",
        text: "",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Tak",
        cancelButtonText: "Anuluj",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await axios({
              method: "post",
              url:
                process.env.VUE_APP_ROOT_API +
                `settlements/revert-all/${this.getWeekDates().startDate}/${this.getWeekDates().endDate}/${this.scheduleSegment}`,
              headers: {
                Authorization: "Bearer " + localStorage.getItem("token"),
              },
            });
            await this.initialize();
          } catch (e) {
            console.log(e);
            Swal.fire("Błąd", e.response.data.message.toString(), "error");
          }
        }
      });
    },
    async finalizeAll() {
      Swal.fire({
        title: "Napewno oznaczyc wszystkie rozliczenia jako wypłacone?",
        text: "",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Tak",
        cancelButtonText: "Anuluj",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            const resp = await axios({
              method: "post",
              url:
                process.env.VUE_APP_ROOT_API +
                `settlements/finalize-all/${this.getWeekDates().startDate}/${this.getWeekDates().endDate}/${this.scheduleSegment}`,
              headers: {
                Authorization: "Bearer " + localStorage.getItem("token"),
              },
            });
            await this.initialize();
            this.pokazWynikMasowejWyplaty(resp, "Rozliczenia oznaczone jako wypłacone");
          } catch (e) {
            console.log(e);
            Swal.fire("Błąd", e.response.data.message.toString(), "error");
          }
        }
      });
    },

    // Masowe oznaczanie POMIJA B2B bez zaakceptowanej faktury — biuro musi to
    // zobaczyć, bo inaczej „wszystkie wypłacone" brzmi jak „wszystkie", a kierowca
    // zostaje bez przelewu w ciszy (tak zginął tydzień Nowosadki, 4 212,58 zł).
    pokazWynikMasowejWyplaty(resp, tytulOk) {
      const dane = (resp && resp.data && resp.data.data) || {};
      const pominieci = Array.isArray(dane.skippedB2B) ? dane.skippedB2B : [];
      const ile = Number(dane.count || 0);
      if (!pominieci.length) {
        Swal.fire("Sukces", `${tytulOk} (${ile})`, "success");
        return;
      }
      const wiersze = pominieci
        .map((x) => {
          const kwota = (Number(x.amount || 0) / 100).toLocaleString("pl-PL", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
          const data = x.date ? new Date(x.date).toLocaleDateString("pl-PL") : "";
          const powod =
            x.powod === "FAKTURA_NIEZAAKCEPTOWANA"
              ? "faktura czeka na akceptację"
              : "brak faktury";
          return `<li><b>${x.driver || "—"}</b> — ${kwota} zł, ${data} (${powod})</li>`;
        })
        .join("");
      Swal.fire({
        icon: "warning",
        title: `${tytulOk}: ${ile}`,
        html:
          `<p style="text-align:left">Pominięto <b>${pominieci.length}</b> rozliczeń B2B — wypłata czeka na fakturę:</p>` +
          `<ul style="text-align:left">${wiersze}</ul>` +
          `<p style="text-align:left">Po wgraniu i akceptacji faktury oznacz te wypłaty osobno.</p>`,
      });
    },

    async markAsPaid(id) {
      if (this.rowBusy[id]) return;
      const result = await Swal.fire({
        title: "Oznaczyć jako wypłacone (przelew)?",
        text: "",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Tak",
        cancelButtonText: "Anuluj",
      });
      if (!result.isConfirmed) return;
      this.$set(this.rowBusy, id, 'markPaid');
      try {
        await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "settlements/finalize/" + id,
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        await this.initialize();
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response?.data?.message?.toString() || "Wystąpił błąd", "error");
      } finally {
        this.$delete(this.rowBusy, id);
      }
    },

    // Wyczyszczenie pola podpisu — kierowcy czasem nie wychodzi i musieli
    // zamykać cały dialog i zaczynać od nowa.
    clearCashSig(which) {
      const pad = which === 'driver'
        ? this.$refs.cashDriverSignaturePad
        : this.$refs.cashWorkerSignaturePad;
      try { pad?.clearSignature(); } catch (e) { /* ignore */ }
      this.cashSig[which] = null;
    },
    onCashSigEnd(which) {
      const pad = which === 'driver'
        ? this.$refs.cashDriverSignaturePad
        : this.$refs.cashWorkerSignaturePad;
      try {
        if (pad) {
          const r = pad.saveSignature();
          if (r && !r.isEmpty) this.cashSig[which] = r.data;
        }
      } catch (e) { console.warn('cash signature capture failed', e); }
    },
    async loadCashSubCompanies() {
      try {
        const res = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "cash-report/sub-companies",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        this.cashSubCompanies = (res.data && res.data.data) || [];
        const bydgoszcz = this.cashSubCompanies.find(
          (s) => (s.name || "").toLowerCase().includes("bydgoszcz"),
        );
        this.cashSubCompanyId = bydgoszcz
          ? bydgoszcz.id
          : this.cashSubCompanies[0]
          ? this.cashSubCompanies[0].id
          : null;
      } catch (e) {
        console.warn("load cash sub-companies failed", e);
        this.cashSubCompanies = [];
        this.cashSubCompanyId = null;
      }
    },
    // Kilka zaległych wypłat jednego kierowcy naraz: jeden podpis, jedno
    // pokwitowanie z rozpisanymi okresami. Wcześniej trzeba było przejść tę
    // samą ścieżkę osobno dla każdego rozliczenia.
    async loadSignatureDevices() {
      try {
        const res = await axios.get(process.env.VUE_APP_ROOT_API + 'signature/devices', {
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        // Interesują nas tylko urządzenia realnie sparowane — te z samym kodem
        // czekają jeszcze na wpisanie go na tablecie.
        // Sortujemy od ostatnio widzianego. Panel bierze z tej listy pierwszy
        // element, a wczesniej kolejnosc szla od NAJSTARSZEGO wpisu — wiec po
        // przeparowaniu tabletu (nowy rekord, ta sama nazwa) zadania lecialy na
        // martwy wpis i okno podpisu nigdy sie nie otwieralo. 30.09.2026
        // zablokowalo to podpisy na tydzien. Backend sortuje tak samo; tu
        // powtarzamy to u siebie, zeby nie zalezec od kolejnosci z serwera.
        const wgOstatnioWidzianego = (a, b) =>
          new Date(b.lastSeenAt || 0) - new Date(a.lastSeenAt || 0);
        this.signatureDevices = (res.data.data || [])
          .filter((d) => d.pairedAt)
          .sort(wgOstatnioWidzianego);
      } catch (e) {
        this.signatureDevices = [];
      }
    },
    async startTabletPairing() {
      this.tabletError = '';
      try {
        const res = await axios.post(
          process.env.VUE_APP_ROOT_API + 'signature/devices/pair-code',
          { name: 'Tablet do podpisu' },
          { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } },
        );
        this.tabletPairing = res.data.data.pairingCode;
      } catch (e) {
        this.tabletError = e.response?.data?.message || 'Nie udało się wygenerować kodu';
      }
    },
    /** Wysyła na tablet to, co kierowca ma podpisać, i czeka na podpis. */
    /**
     * Tablet, na ktory wysylamy prosbe o podpis: ten, ktory odezwal sie
     * NAJPOZNIEJ. Lista jest juz tak posortowana, ale wybor nazywamy wprost,
     * bo wczesniej stalo tu `signatureDevices[0]` i przy dwoch wpisach o tej
     * samej nazwie (po przeparowaniu) trafialo to w martwy.
     */
    tabletDoPodpisu() {
      return (this.signatureDevices || [])
        .slice()
        .sort((a, b) => new Date(b.lastSeenAt || 0) - new Date(a.lastSeenAt || 0))[0];
    },
    async requestTabletSignature() {
      this.tabletError = '';
      const current = (this.items || []).find(
        (i) => i.id === this.signatureDialog.settlementId,
      );
      const zalegle = (this.signatureDialog.related || []).filter((r) => r.checked);
      const items = [
        ...(current ? [{ date: current.settlementDate, amount: current.kwotaDoRekiRaw || 0 }] : []),
        ...zalegle.map((r) => ({ date: r.date, amount: r.amount })),
      ];
      try {
        const res = await axios.post(
          process.env.VUE_APP_ROOT_API + 'signature/requests',
          {
            deviceId: this.tabletDoPodpisu().id,
            driverName: current ? current.driver : '',
            totalCents: this.cashDialogDoReki,
            items,
          },
          { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } },
        );
        this.tabletRequestId = res.data.data.id;
        this.tabletWaiting = true;
        this.pollTabletSignature();
      } catch (e) {
        this.tabletError = e.response?.data?.message || 'Nie udało się wysłać prośby na tablet';
      }
    },
    pollTabletSignature() {
      clearInterval(this.tabletPoll);
      this.tabletPoll = setInterval(async () => {
        if (!this.tabletRequestId) return;
        try {
          const res = await axios.get(
            process.env.VUE_APP_ROOT_API + 'signature/requests/' + this.tabletRequestId,
            { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } },
          );
          const d = res.data.data;
          if (d.status === 'SIGNED' && d.signature) {
            // Podpis z tabletu wchodzi tak samo jak złożony na komputerze —
            // dalsza część wypłaty nie musi wiedzieć, skąd pochodzi.
            this.cashSig.driver = d.signature;
            this.stopTabletWaiting();
          } else if (d.status === 'EXPIRED' || d.status === 'CANCELLED') {
            this.tabletError = 'Prośba wygasła — poproś o podpis ponownie';
            this.stopTabletWaiting();
          }
        } catch (e) {
          this.tabletError = 'Utracono połączenie z tabletem';
          this.stopTabletWaiting();
        }
      }, 2000);
    },
    stopTabletWaiting() {
      clearInterval(this.tabletPoll);
      this.tabletPoll = null;
      this.tabletWaiting = false;
      this.tabletRequestId = null;
    },
    async cancelTabletSignature() {
      const id = this.tabletRequestId;
      this.stopTabletWaiting();
      if (!id) return;
      try {
        await axios.post(
          process.env.VUE_APP_ROOT_API + 'signature/requests/' + id + '/cancel',
          {},
          { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } },
        );
      } catch (e) { /* anulowanie nie może blokować wypłaty */ }
    },
    markAsPaidCashBatch() {
      if (!this.cashBatchReady) return;
      this.signatureDialog.batchIds = this.cashBatchCandidates.map((r) => r.id);
      this.markAsPaidCash(null);
    },
    markAsPaidCash(id) {
      this.signatureDialog.settlementId = id;
      if (id) this.signatureDialog.batchIds = null;
      // Kierowca przychodzi po pieniądze raz — jeśli ma jeszcze niewypłacone
      // gotówki z wcześniejszych tygodni, pokazujemy je od razu. Bez tego
      // trzeba było pamiętać, żeby poszukać ich w tabeli.
      this.signatureDialog.related = [];
      if (id) {
        const current = (this.items || []).find((i) => i.id === id);
        if (current && current.driverId) {
          this.signatureDialog.related = (this.items || [])
            .filter(
              (i) =>
                i.id !== id &&
                i.driverId === current.driverId &&
                i.payoutTypeRaw === 'CASH' &&
                !i.isSettledRaw,
            )
            .map((i) => ({
              id: i.id,
              date: i.settlementDate,
              amount: i.kwotaDoRekiRaw || 0,
              // Domyślnie dołączone — zwykle wydaje się wszystko naraz,
              // a odznaczyć zawsze można.
              checked: true,
            }));
        }
      }
      this.signatureDialog.show = true;
      this.cashSig = { driver: null, worker: null };
      this.loadCashSubCompanies();
      this.loadSignatureDevices();
      this.tabletError = '';
      this.tabletPairing = null;
      this.stopTabletWaiting();
      // force=true: twardy reset+rozmiar (przy otwarciu, pad i tak pusty).
      // force=false (opóźnione): NIE czyść jeśli ktoś już podpisał — wcześniej
      // setTimeout(...,300) wycierał podpis złożony w <300 ms (tablet).
      const resize = (force) => {
        try {
          for (const pad of [
            this.$refs.cashDriverSignaturePad,
            this.$refs.cashWorkerSignaturePad,
          ]) {
            if (!pad) continue;
            const signed = pad.isEmpty ? !pad.isEmpty() : false;
            if (!force && signed) continue; // user już podpisuje — nie ruszaj
            if (pad.clearSignature) pad.clearSignature();
            if (pad.resizeCanvas) pad.resizeCanvas();
          }
        } catch (e) { console.warn('signature pad resize failed', e); }
      };
      this.$nextTick(() => resize(true));
      setTimeout(() => resize(false), 300);
    },
    async submitCashPayment(withPrint, withoutSignature = false) {
      if (!this.cashSubCompanyId) {
        Swal.fire("Wybierz oddział", "Wskaż oddział, na który ma trafić wpis kasowy.", "warning");
        return;
      }
      // Źródło prawdy: podpis przechwycony na bieżąco w onEnd (odporny na
      // resize/clear pada). Fallback: odczyt na żywo tuż przed wysyłką.
      // withoutSignature = świadomy zapis pokwitowania bez podpisu.
      let signature = withoutSignature ? null : this.cashSig.driver;
      let workerSignature = withoutSignature ? null : this.cashSig.worker;
      if (!withoutSignature) {
        try {
          const driverPad = this.$refs.cashDriverSignaturePad;
          if (!signature && driverPad) {
            const res = driverPad.saveSignature();
            if (res && !res.isEmpty) signature = res.data;
          }
        } catch (e) { console.warn('driver signature capture failed', e); }
        try {
          const workerPad = this.$refs.cashWorkerSignaturePad;
          if (!workerSignature && workerPad) {
            const res = workerPad.saveSignature();
            if (res && !res.isEmpty) workerSignature = res.data;
          }
        } catch (e) { console.warn('worker signature capture failed', e); }
      }

      // Wypłata zbiorcza powstaje na dwa sposoby: z zaznaczenia w tabeli albo
      // z zaległych dociągniętych przy pojedynczej wypłacie.
      const zaznaczoneZalegle = (this.signatureDialog.related || [])
        .filter((r) => r.checked)
        .map((r) => r.id);
      const batchIds =
        this.signatureDialog.batchIds ||
        (zaznaczoneZalegle.length
          ? [this.signatureDialog.settlementId, ...zaznaczoneZalegle]
          : null);
      try {
        const res = await axios({
          method: "post",
          url: batchIds
            ? process.env.VUE_APP_ROOT_API + "settlements/mark-paid-cash-batch"
            : process.env.VUE_APP_ROOT_API + "settlements/mark-paid-cash/" + this.signatureDialog.settlementId,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
          data: batchIds
            ? { settlementIds: batchIds, signature, workerSignature, subCompanyId: this.cashSubCompanyId }
            : { signature, workerSignature, subCompanyId: this.cashSubCompanyId },
        });
        this.signatureDialog.show = false;
        this.signatureDialog.batchIds = null;
        this.signatureDialog.related = [];
        this.stopTabletWaiting();
        this.selectedRows = [];
        if (withPrint) {
          if (batchIds) {
            // Zbiorcze pokwitowanie składa backend — jeden dokument z rozpisanymi
            // okresami i sumą, zamiast kilku osobnych wydruków.
            this.openGroupReceipt(res.data.data.receiptGroupId);
          } else {
            this.printReceipt(res.data.data);
          }
        }
        await Promise.all([this.initialize(), this.fetchReceipts()]);
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response?.data?.message?.toString() || "Wystąpił błąd", "error");
      }
    },
    async openGroupReceipt(groupId) {
      if (!groupId) return;
      try {
        const res = await axios({
          method: 'get',
          url: process.env.VUE_APP_ROOT_API + 'cash-report/receipt-group/' + groupId,
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
          responseType: 'blob',
        });
        const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        window.open(url, '_blank');
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      } catch (e) {
        Swal.fire('Błąd', 'Nie udało się pobrać pokwitowania zbiorczego', 'error');
      }
    },
    printReceipt(data) {
      const formatPLN = (v) => ((v || 0) / 100).toFixed(2) + ' PLN';
      const dateStr = data.date ? new Date(data.date).toLocaleDateString('pl-PL') : new Date().toLocaleDateString('pl-PL');
      const nowStr = new Date().toLocaleString('pl-PL');
      const isB2B = data.cooperationType === 'B2B_VAT' || data.cooperationType === 'B2B_NO_VAT';

      let transferRows = '';
      if (isB2B) {
        transferRows = `<tr><td>Kwota z tyt. faktury</td><td style="text-align:right">${formatPLN(data.netPayout)}</td></tr>`;
      } else {
        if (data.zleceniowkaAmount) transferRows += `<tr><td>Zleceniówka</td><td style="text-align:right">${formatPLN(data.zleceniowkaAmount)}</td></tr>`;
        if (data.costInvoiceReimbursement) transferRows += `<tr><td>Zwrot kosztów</td><td style="text-align:right">${formatPLN(data.costInvoiceReimbursement)}</td></tr>`;
        if (data.rentTransferAmount) transferRows += `<tr><td>Najem</td><td style="text-align:right">${formatPLN(data.rentTransferAmount)}</td></tr>`;
      }

      const signatureHtml = data.signature
        ? `<div class="sig-block"><img src="${data.signature}" style="max-width:250px;max-height:120px;" /><div class="signature-label">Podpis odbiorcy</div></div>`
        : `<div class="sig-block"><div class="signature-line"></div><div class="signature-label">Podpis odbiorcy</div></div>`;
      const workerSignatureHtml = data.workerSignature
        ? `<div class="sig-block"><img src="${data.workerSignature}" style="max-width:250px;max-height:120px;" /><div class="signature-label">Podpis wypłacającego</div></div>`
        : `<div class="sig-block"><div class="signature-line"></div><div class="signature-label">Podpis wypłacającego</div></div>`;

      const html = `
        <html>
        <head>
          <meta charset="UTF-8">
          <title>Pokwitowanie wypłaty gotówkowej</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; font-size: 14px; }
            h2 { text-align: center; margin-bottom: 30px; }
            .header { text-align: center; margin-bottom: 20px; color: #666; font-size: 12px; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            td { padding: 8px 12px; border-bottom: 1px solid #eee; }
            .total td { font-weight: bold; border-top: 2px solid #333; border-bottom: none; font-size: 16px; }
            .signatures { display: flex; justify-content: space-between; margin-top: 60px; }
            .sig-block { width: 250px; text-align: center; }
            .signature-line { width: 250px; height: 80px; border-bottom: 1px solid #333; }
            .signature-label { margin-top: 5px; font-size: 12px; color: #666; }
            @media print { body { padding: 20px; } }
          </style>
        </head>
        <body>
          <div class="header">${data.companyName}<br>${data.companyAddress}</div>
          <h2>Pokwitowanie wypłaty gotówkowej</h2>
          <table>
            <tr><td>Data rozliczenia</td><td style="text-align:right">${dateStr}</td></tr>
            <tr><td>Data wystawienia</td><td style="text-align:right">${nowStr}</td></tr>
            <tr><td>Odbiorca</td><td style="text-align:right"><strong>${data.driverName}</strong></td></tr>
            <tr><td>Typ współpracy</td><td style="text-align:right">${this.formatCooperationType(data.cooperationType)}</td></tr>
          </table>
          <table>
            ${transferRows}
            <tr class="total"><td>RAZEM DO WYPŁATY</td><td style="text-align:right">${formatPLN(data.totalToPayout)}</td></tr>
          </table>
          <p style="margin-top:30px; font-size:12px; color:#666;">
            Potwierdzam odbiór powyższej kwoty w gotówce.
          </p>
          <div class="signatures">
            ${workerSignatureHtml}
            ${signatureHtml}
          </div>
        </body>
        </html>
      `;

      const printWindow = window.open('', '_blank', 'width=800,height=600');
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    },

    async markAllWirePaid() {
      Swal.fire({
        title: "Oznaczyć wszystkie przelewy jako wypłacone?",
        text: "Dotyczy tylko rozliczeń przelewowych (bez gotówkowych)",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Tak",
        cancelButtonText: "Anuluj",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            const resp = await axios({
              method: "post",
              url:
                process.env.VUE_APP_ROOT_API +
                `settlements/mark-all-wire-paid/${this.getWeekDates().startDate}/${this.getWeekDates().endDate}/${this.scheduleSegment}`,
              headers: {
                Authorization: "Bearer " + localStorage.getItem("token"),
              },
            });
            await this.initialize();
            this.pokazWynikMasowejWyplaty(resp, "Przelewy oznaczone jako wypłacone");
          } catch (e) {
            console.log(e);
            Swal.fire("Błąd", e.response?.data?.message?.toString() || "Wystąpił błąd", "error");
          }
        }
      });
    },

    async markInvoiceUploaded(id) {
      Swal.fire({
        title: "Oznaczyć fakturę jako wgraną?",
        text: "Po oznaczeniu będzie możliwa wypłata",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Tak",
        cancelButtonText: "Anuluj",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await axios({
              method: "post",
              url: process.env.VUE_APP_ROOT_API + "settlements/mark-invoice-uploaded/" + id,
              headers: {
                Authorization: "Bearer " + localStorage.getItem("token"),
              },
            });
            await this.initialize();
          } catch (e) {
            console.log(e);
            Swal.fire("Błąd", e.response?.data?.message?.toString() || "Wystąpił błąd", "error");
          }
        }
      });
    },

    // Akceptacja/odrzucenie faktury B2B wgranej przez kierowcę.
    async reviewInvoice(item, action) {
      let reason;
      if (action === 'REJECT') {
        const res = await Swal.fire({
          title: this.$t('payouts.rejectReasonTitle'),
          input: 'textarea',
          inputPlaceholder: this.$t('payouts.rejectReasonHint'),
          inputAttributes: { maxlength: 500 },
          showCancelButton: true,
          confirmButtonColor: '#d33',
          confirmButtonText: this.$t('payouts.rejectInvoice'),
          cancelButtonText: 'Anuluj',
          inputValidator: (v) => (!v || !v.trim() ? this.$t('payouts.rejectReasonTitle') : undefined),
        });
        if (!res.isConfirmed) return;
        reason = res.value.trim();
      } else {
        const res = await Swal.fire({
          title: this.$t('payouts.acceptInvoice'),
          text: this.$t('payouts.acceptInvoiceConfirm'),
          icon: 'question',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          confirmButtonText: 'Tak',
          cancelButtonText: 'Anuluj',
        });
        if (!res.isConfirmed) return;
      }
      try {
        await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'settlements/review-invoice/' + item.id,
          data: { action, reason },
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        await this.initialize();
      } catch (e) {
        console.log(e);
        Swal.fire('Błąd', e.response?.data?.message?.toString() || 'Wystąpił błąd', 'error');
      }
    },

    openUploadInvoiceDialog(settlementId) {
      this.uploadInvoiceSettlementId = settlementId;
      this.invoiceFile = null;
      this.uploadInvoiceDialog = true;
    },

    async uploadInvoice() {
      if (!this.invoiceFile || !this.uploadInvoiceSettlementId) return;
      try {
        const formData = new FormData();
        formData.append("file", this.invoiceFile);
        await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "settlements/upload-invoice/" + this.uploadInvoiceSettlementId,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
            "Content-Type": "multipart/form-data",
          },
          data: formData,
        });
        this.uploadInvoiceDialog = false;
        Swal.fire("Sukces", "Faktura wgrana", "success");
        await this.initialize();
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response?.data?.message?.toString() || "Wystąpił błąd", "error");
      }
    },

    async previewInvoice(fileId) {
      this.previewInvoiceDialog = true;
      this.previewLoading = true;
      this.previewUrl = null;
      try {
        const response = await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "aws/download-file/" + fileId,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
          responseType: "blob",
        });
        const blob = new Blob([response.data], { type: response.headers["content-type"] });
        this.previewUrl = URL.createObjectURL(blob);
        this.previewIsPdf = response.headers["content-type"]?.includes("pdf");
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", "Nie udało się pobrać podglądu", "error");
        this.previewInvoiceDialog = false;
      }
      this.previewLoading = false;
    },

    async openOnDemand() {
      this.onDemandDriverId = null;
      this.onDemandDialog = true;
      if (this.onDemandDrivers.length) return;
      this.onDemandLoadingDrivers = true;
      try {
        const res = await axios({
          method: 'get',
          url: process.env.VUE_APP_ROOT_API + 'drivers?take=10000&skip=1',
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        const list = res.data?.data?.result || res.data?.result || [];
        this.onDemandDrivers = list
          .map((d) => ({ id: d.id, name: `${d.firstName} ${d.lastName}` }))
          .sort((a, b) => a.name.localeCompare(b.name, 'pl'));
      } catch (e) {
        console.log(e);
      } finally {
        this.onDemandLoadingDrivers = false;
      }
    },
    async submitOnDemand() {
      this.onDemandSending = true;
      try {
        const res = await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'settlements/on-demand/' + this.onDemandDriverId,
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        const s = res.data?.data || {};
        this.onDemandDialog = false;
        Swal.fire('Wypłata wygenerowana', `Rozliczenie na żądanie: ${((s.totalToPayout || 0) / 100).toFixed(2)} zł (w tym opłata −50 zł). Sprawdź i opublikuj.`, 'success');
        await this.initialize();
      } catch (e) {
        Swal.fire('Nie można wygenerować', e.response?.data?.message?.toString() || 'Błąd generowania wypłaty na żądanie', 'error');
      } finally {
        this.onDemandSending = false;
      }
    },
    async generateTransfers() {
      this.generatingTransfers = true;
      // Nazwę nadawcy pamiętamy, żeby biuro nie wpisywało jej przy każdej
      // paczce. Zapis w try/catch: prywatne okno albo zablokowane dane stron
      // nie mogą przerwać generowania przelewów.
      try {
        localStorage.setItem('payoutBasketSender', this.transferConfig.senderName || '');
      } catch (e) { /* brak zapisu znaczy tylko, że trzeba wpisać ponownie */ }
      try {
        const { startDate, endDate } = this.getWeekDates();
        const response = await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "settlements/generate-transfers",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
          data: {
            startDate,
            endDate,
            payoutSchedule: this.filters.settlementType,
            bankFormat: this.transferConfig.bankFormat,
            senderAccountNo: this.transferConfig.senderAccountNo,
            senderName: this.transferConfig.senderName,
          },
        });
        const files = response.data.data;
        const basketLabels = {
          zleceniowka: 'zleceniowka',
          zwrot_kosztow: 'zwrot-kosztow',
          najem: 'najem',
          komornik: 'komornik',
          zaliczki_dzienne: 'zaliczki-dzienne',
        };
        const fmt = this.transferConfig.bankFormat.toLowerCase();
        // Wszystkie koszyki pakujemy w JEDEN plik ZIP do rozpakowania.
        const JSZip = (await import("jszip")).default;
        const zip = new JSZip();
        let count = 0;
        for (const [key, content] of Object.entries(files)) {
          if (!content) continue;
          zip.file(`${basketLabels[key] || key}_${startDate}_${fmt}.csv`, content);
          count++;
        }
        this.transferDialog = false;
        if (count === 0) {
          Swal.fire("Info", "Brak przelewów do wygenerowania (sprawdź czy wypłaty są opublikowane i czy B2B ma wgraną i zaakceptowaną fakturę)", "info");
        } else {
          const zipBlob = await zip.generateAsync({ type: "blob" });
          const url = window.URL.createObjectURL(zipBlob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `koszyki-przelewow_${startDate}_${fmt}.zip`;
          a.click();
          window.URL.revokeObjectURL(url);
          Swal.fire("Sukces", `Wygenerowano ZIP z ${count} koszykiem(-ami) przelewów`, "success");
        }
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response?.data?.message?.toString() || "Wystąpił błąd", "error");
      }
      this.generatingTransfers = false;
    },

    async publishSettlement(id) {
      if (this.rowBusy[id]) return;
      const result = await Swal.fire({
        title: "Opublikować rozliczenie kierowcy?",
        text: "Kierowca zobaczy je w aplikacji.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Opublikuj",
        cancelButtonText: "Anuluj",
      });
      if (!result.isConfirmed) return;
      this.$set(this.rowBusy, id, 'publish');
      try {
        await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "settlements/publish/" + id,
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        await this.initialize();
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response?.data?.message?.toString() || "Wystąpił błąd", "error");
      } finally {
        this.$delete(this.rowBusy, id);
      }
    },

    async unpublishSettlement(id) {
      if (this.rowBusy[id]) return;
      const result = await Swal.fire({
        title: "Cofnąć publikację?",
        text: "Kierowca przestanie widzieć rozliczenie w aplikacji.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Cofnij publikację",
        cancelButtonText: "Anuluj",
      });
      if (!result.isConfirmed) return;
      this.$set(this.rowBusy, id, 'unpublish');
      try {
        await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "settlements/unpublish/" + id,
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        await this.initialize();
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response?.data?.message?.toString() || "Wystąpił błąd", "error");
      } finally {
        this.$delete(this.rowBusy, id);
      }
    },

    async publishAll() {
      Swal.fire({
        title: "Opublikować wszystkie rozliczenia?",
        text: "Kierowcy zobaczą swoje rozliczenia",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Tak",
        cancelButtonText: "Anuluj",
      }).then(async (result) => {
        if (result.isConfirmed) {
          this.publishingAll = true;
          try {
            const res = await axios({
              method: "post",
              url:
                process.env.VUE_APP_ROOT_API +
                `settlements/publish-all/${this.getWeekDates().startDate}/${this.getWeekDates().endDate}/${this.scheduleSegment}`,
              headers: {
                Authorization: "Bearer " + localStorage.getItem("token"),
              },
            });
            await this.initialize();
            const cnt = res.data?.data?.count ?? 0;
            if (cnt > 0) {
              Swal.fire("Sukces", `Opublikowano ${cnt} rozliczeń. Kierowcy widzą je teraz w aplikacji.`, "success");
            } else {
              Swal.fire({
                title: "Nic nie opublikowano",
                html: "Brak rozliczeń do opublikowania w tym tygodniu — albo wszystkie są już opublikowane, albo nie ma jeszcze utworzonych rozliczeń. Sprawdź czy w tabeli jest kolumna <b>'Opublikowane?'</b> = 'Nie' dla tych kierowców których chcesz opublikować.",
                icon: "warning",
              });
            }
          } catch (e) {
            console.log(e);
            Swal.fire("Błąd", e.response?.data?.message?.toString() || "Wystąpił błąd", "error");
          } finally {
            // Zawsze odblokuj przycisk — wcześniej reset był tylko w catch,
            // więc po udanej publikacji flaga zostawała true i przycisk
            // był wyszarzały aż do pełnego odświeżenia strony.
            this.publishingAll = false;
          }
        }
      });
    },

    async unpublishAll() {
      Swal.fire({
        title: "Ukryć wszystkie rozliczenia?",
        text: "Kierowcy nie będą widzieć swoich rozliczeń",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Tak",
        cancelButtonText: "Anuluj",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await axios({
              method: "post",
              url:
                process.env.VUE_APP_ROOT_API +
                `settlements/unpublish-all/${this.getWeekDates().startDate}/${this.getWeekDates().endDate}/${this.scheduleSegment}`,
              headers: {
                Authorization: "Bearer " + localStorage.getItem("token"),
              },
            });
            this.publishingAll = false;
            await this.initialize();
            Swal.fire("Sukces", "Rozliczenia ukryte", "success");
          } catch (e) {
            console.log(e);
            Swal.fire("Błąd", e.response?.data?.message?.toString() || "Wystąpił błąd", "error");
          }
        }
      });
    },

    // Receipts tab methods
    getReceiptWeekDates() {
      const [yearStr, weekStr] = this.receipts.filters.selectedWeek.split('-');
      const week = parseInt(weekStr.replace('W', ''));
      const year = parseInt(yearStr);
      const jan4 = dayjs(`${year}-01-04`);
      const dayOfWeek = jan4.day();
      const firstMonday = jan4.subtract((dayOfWeek + 6) % 7, 'day');
      const start = firstMonday.add((week - 1) * 7, 'day');
      const end = start.add(6, 'day');
      return { startDate: start.format('YYYY-MM-DD'), endDate: end.format('YYYY-MM-DD') };
    },
    async fetchReceipts() {
      this.receipts.loading = true;
      try {
        const { startDate, endDate } = this.getReceiptWeekDates();
        const params = {
          startDate,
          endDate,
          payoutSchedule: this.receipts.filters.settlementType,
        };
        if (this.receipts.filters.subCompany && this.receipts.filters.subCompany.length > 0) {
          params.subCompanyIds = this.receipts.filters.subCompany;
        }
        const res = await axios.get(
          process.env.VUE_APP_ROOT_API + `settlements/cash-receipts?take=${this.receipts.options.itemsPerPage}&skip=${this.receipts.options.page}&search=${this.receipts.search || ''}`,
          {
            params,
            paramsSerializer: (params) => {
              const searchParams = new URLSearchParams();
              for (const key of Object.keys(params)) {
                const value = params[key];
                if (Array.isArray(value)) {
                  value.forEach((v) => searchParams.append(key, v));
                } else if (value !== null && value !== undefined) {
                  searchParams.append(key, value);
                }
              }
              return searchParams.toString();
            },
            headers: { Authorization: "Bearer " + localStorage.getItem("token") },
          }
        );
        this.receipts.items = res.data.data.items;
        this.receipts.total = res.data.data.total;
      } catch (e) {
        console.log(e);
      }
      this.receipts.loading = false;
    },
    async reprintReceipt(id) {
      try {
        const res = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "settlements/cash-receipt/" + id,
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        this.printReceipt(res.data.data);
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response?.data?.message?.toString() || "Wystąpił błąd", "error");
      }
    },
  },
};
</script>
<style scoped>
/* === Driver-app aesthetic for admin Payouts ===
   Wybijamy się z 24px padding'u .btp-main żeby tło #181C28 dochodziło
   do krawędzi obszaru. */
.payouts-page {
  background: #181C28;
  min-height: calc(100vh - 56px);
  padding: 16px 16px 40px;
  margin: -24px;
  color: #fff;
}
.payouts-page * { box-sizing: border-box; }

/* Page header */
.page-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  padding: 4px 4px 18px;
}
.page-eyebrow {
  font-size: 12px;
  color: #6E7691;
  font-weight: 500;
  text-transform: capitalize;
}
.page-title {
  font-size: 22px;
  font-weight: 800;
  color: #fff;
  margin: 2px 0 0;
  letter-spacing: -0.02em;
}

/* Segmented tabs */
.tabs-pill {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  background: #232838;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 999px;
  padding: 6px;
  margin-bottom: 16px;
  width: fit-content;
  max-width: 100%;
}
.tab-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  color: #B0BEC5;
  padding: 8px 14px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: -0.01em;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
.tab-pill:hover { color: #fff; }
.tab-pill--active {
  background: #2962FF;
  color: #fff;
  box-shadow: 0 4px 12px rgba(41, 98, 255, 0.35);
}
.tab-pill--active >>> .v-icon { color: #fff !important; }
.tab-badge {
  background: rgba(255, 152, 0, 0.25);
  color: #FFB74D;
  font-size: 11px;
  font-weight: 700;
  padding: 1px 7px;
  border-radius: 999px;
  min-width: 20px;
  text-align: center;
}
.tab-pill--active .tab-badge {
  background: rgba(255, 255, 255, 0.25);
  color: #fff;
}

/* Pool panel */
.pool-panel {
  background: #232838;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 18px;
  padding: 16px 18px;
}
.pool-panel-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}
.pool-icon {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  background: rgba(41, 98, 255, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.pool-title {
  font-size: 15px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.01em;
  flex-grow: 1;
}
.pool-expand-btn {
  background: #181C28;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 999px;
  color: #B0BEC5;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.pool-expand-btn:hover { color: #fff; border-color: rgba(255,255,255,0.12); }
.pool-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 10px;
}
.pool-stat {
  background: #181C28;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 14px;
  padding: 12px 14px;
}
.pool-stat-icon {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
}
.pool-stat--blue .pool-stat-icon   { background: rgba(41, 98, 255, 0.18); }
.pool-stat--orange .pool-stat-icon { background: rgba(255, 152, 0, 0.16); }
.pool-stat--green .pool-stat-icon  { background: rgba(45, 203, 115, 0.16); }
.pool-stat--grey .pool-stat-icon   { background: rgba(144, 164, 174, 0.16); }
.pool-stat-label {
  font-size: 11px;
  color: #6E7691;
  font-weight: 500;
  margin-bottom: 4px;
}
.pool-stat-value {
  font-size: 18px;
  font-weight: 800;
  color: #fff;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
}
.pool-body { color: #B0BEC5; }

/* === Reusable: filter section, summary grid, action pills, search === */
.filter-section {
  background: #232838;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 18px;
  padding: 16px 18px;
}
.filter-section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}
.filter-icon {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: rgba(41, 98, 255, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.filter-title {
  font-size: 15px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.01em;
  flex-grow: 1;
}
.table-section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

/* Bulk actions row */
.bulk-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 14px 14px 6px;
}
.action-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #181C28;
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #fff;
  padding: 8px 14px;
  border-radius: 10px;
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: -0.01em;
  cursor: pointer;
  transition: transform 0.12s ease, background 0.15s ease, border-color 0.15s ease;
  font-family: inherit;
}
.action-pill:hover { transform: translateY(-1px); border-color: rgba(255,255,255,0.18); }
.action-pill:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
.action-pill .v-icon { color: inherit !important; }
.action-pill--primary { background: #2962FF; border-color: #2962FF; box-shadow: 0 4px 12px rgba(41,98,255,0.3); }
.action-pill--info    { background: rgba(66, 165, 245, 0.18); border-color: rgba(66,165,245,0.35); color: #64B5F6; }
.action-pill--success { background: rgba(45, 203, 115, 0.18); border-color: rgba(45,203,115,0.35); color: #6FE89A; }
.action-pill--warning { background: rgba(255, 152, 0, 0.18); border-color: rgba(255,152,0,0.35); color: #FFB74D; }
.action-pill--error   { background: rgba(244, 67, 54, 0.18); border-color: rgba(244,67,54,0.35); color: #FF8A80; }
.action-pill--neutral { background: rgba(255, 255, 255, 0.06); border-color: rgba(255,255,255,0.12); color: #B0BEC5; }

/* Search input */
.search-row { padding: 8px 14px 14px; }
.search-input {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #181C28;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 10px 14px;
}
.search-input input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: #fff;
  font-size: 14px;
  font-family: inherit;
}
.search-input input::placeholder { color: #6E7691; }

/* Summary grid (BLIK) */
.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 10px;
  background: #232838;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 18px;
  padding: 16px;
}
.summary-stat {
  background: #181C28;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 14px;
  padding: 12px 14px;
}
.summary-icon {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
}
.summary-label {
  font-size: 11px;
  color: #6E7691;
  font-weight: 500;
  margin-bottom: 4px;
}
.summary-value {
  font-size: 18px;
  font-weight: 800;
  color: #fff;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
}

/* === Vuetify deep overrides === */
/* Cards (dialogs etc.) */
.payouts-page >>> .v-card {
  background: #232838 !important;
  border: 1px solid rgba(255, 255, 255, 0.06) !important;
  border-radius: 18px !important;
  box-shadow: none !important;
  color: #fff !important;
}
.payouts-page >>> .v-card .v-card__title {
  font-size: 14px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.01em;
  padding: 14px 16px 8px;
}
.payouts-page >>> .v-card .v-card__title .v-icon {
  color: #5478FF !important;
}

/* Tabs items wrapper */
.payouts-page >>> .v-window.v-tabs-items,
.payouts-page >>> .v-tabs-items {
  background: transparent !important;
}

/* Data tables — wymuś ciemny styl (nie polegamy na theme--dark) */
.payouts-page >>> .v-data-table {
  background: #232838 !important;
  border: 1px solid rgba(255, 255, 255, 0.06) !important;
  border-radius: 18px !important;
  color: #fff !important;
  box-shadow: none !important;
}
.payouts-page >>> .v-data-table thead tr th {
  background: #1F2434 !important;
  color: #6E7691 !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 1px solid rgba(255,255,255,0.06) !important;
}
.payouts-page >>> .v-data-table tbody tr {
  background: #232838 !important;
}
.payouts-page >>> .v-data-table tbody tr:hover {
  background: #2A3042 !important;
}
.payouts-page >>> .v-data-table tbody tr td {
  color: #fff !important;
  border-bottom: 1px solid rgba(255,255,255,0.04) !important;
}
/* Cały wiersz na zielono gdy kierowca jest wypłacony/rozliczony */
.payouts-page >>> .v-data-table tbody tr.payouts-paid-row,
.payouts-page >>> .v-data-table tbody tr.payouts-paid-row td {
  background: rgba(46, 204, 113, 0.20) !important;
}
.payouts-page >>> .v-data-table tbody tr.payouts-paid-row:hover,
.payouts-page >>> .v-data-table tbody tr.payouts-paid-row:hover td {
  background: rgba(46, 204, 113, 0.30) !important;
}

/* Action toolbar buttons in tab 1 */
.payouts-page >>> .v-toolbar {
  background: transparent !important;
  color: #fff !important;
}
.payouts-page >>> .v-toolbar .v-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  letter-spacing: -0.01em !important;
  font-weight: 600 !important;
}

/* Inputs (filter v-select / v-text-field) */
.payouts-page >>> .v-text-field--outlined fieldset,
.payouts-page >>> .v-select--outlined fieldset {
  border-color: rgba(255,255,255,0.1) !important;
}
.payouts-page >>> .v-text-field--outlined .v-input__slot,
.payouts-page >>> .v-select--outlined .v-input__slot {
  background: #181C28 !important;
  border-radius: 12px !important;
  min-height: 44px !important;
}
.payouts-page >>> .v-label {
  color: #6E7691 !important;
  font-size: 13px !important;
}
.payouts-page >>> .v-text-field input,
.payouts-page >>> .v-select__selection {
  color: #fff !important;
}
.payouts-page >>> .v-input__prepend-inner .v-icon {
  color: #6E7691 !important;
}

/* Data table footer */
.payouts-page >>> .v-data-footer {
  background: #1F2434 !important;
  color: #B0BEC5 !important;
  border-top: 1px solid rgba(255,255,255,0.06) !important;
}
.payouts-page >>> .v-data-footer__select .v-select__selection {
  color: #fff !important;
}

/* Chips inside data tables — kafelkowane (zaokrąglone, semi-tinted) */
.payouts-page >>> .v-chip {
  border-radius: 999px !important;
  font-weight: 700 !important;
  letter-spacing: -0.01em !important;
}
.payouts-page >>> .v-chip.green,
.payouts-page >>> .v-chip[color="green"] {
  background: rgba(45, 203, 115, 0.18) !important;
  color: #6FE89A !important;
}
.payouts-page >>> .v-chip.red,
.payouts-page >>> .v-chip[color="red"] {
  background: rgba(244, 67, 54, 0.18) !important;
  color: #FF8A80 !important;
}
.payouts-page >>> .v-chip.orange,
.payouts-page >>> .v-chip[color="orange"] {
  background: rgba(255, 152, 0, 0.18) !important;
  color: #FFB74D !important;
}
.payouts-page >>> .v-chip.blue,
.payouts-page >>> .v-chip[color="blue"] {
  background: rgba(66, 165, 245, 0.18) !important;
  color: #64B5F6 !important;
}
.payouts-page >>> .v-chip.deep-purple,
.payouts-page >>> .v-chip[color="deep-purple"] {
  background: rgba(167, 139, 250, 0.18) !important;
  color: #C4B5FD !important;
}

/* Row-level action buttons inside table cells */
.payouts-page >>> .v-data-table tbody td .v-btn {
  border-radius: 8px !important;
  text-transform: none !important;
  letter-spacing: -0.01em !important;
  font-weight: 600 !important;
  box-shadow: none !important;
}
.payouts-page >>> .v-data-table tbody td .v-btn.success {
  background: rgba(45, 203, 115, 0.18) !important;
  color: #6FE89A !important;
}
.payouts-page >>> .v-data-table tbody td .v-btn.error {
  background: rgba(244, 67, 54, 0.18) !important;
  color: #FF8A80 !important;
}
.payouts-page >>> .v-data-table tbody td .v-btn.warning {
  background: rgba(255, 152, 0, 0.18) !important;
  color: #FFB74D !important;
}
.payouts-page >>> .v-data-table tbody td .v-btn.info {
  background: rgba(66, 165, 245, 0.18) !important;
  color: #64B5F6 !important;
}
.payouts-page >>> .v-data-table tbody td .v-btn.secondary {
  background: rgba(255, 255, 255, 0.06) !important;
  color: #B0BEC5 !important;
}
.payouts-page >>> .v-data-table tbody td .v-btn .v-icon {
  color: inherit !important;
}

/* Driver link inside table */
.payouts-page >>> .v-data-table a {
  color: #5478FF !important;
}

/* Simple-table (allocations list inside pool panel) */
.payouts-page >>> .v-simple-table {
  background: transparent !important;
}
.payouts-page >>> .v-simple-table table {
  background: transparent !important;
}
.payouts-page >>> .v-simple-table thead tr th {
  background: #181C28 !important;
  color: #6E7691 !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 1px solid rgba(255,255,255,0.06) !important;
}
.payouts-page >>> .v-simple-table tbody tr {
  background: transparent !important;
}
.payouts-page >>> .v-simple-table tbody tr td {
  color: #fff !important;
  border-bottom: 1px solid rgba(255,255,255,0.04) !important;
}

/* Expanded row background */
.payouts-page >>> tr.v-data-table__expanded__content {
  background: #1F2434 !important;
}
.payouts-page >>> tr.v-data-table__expanded__row td {
  background: #2A3042 !important;
}

/* Loading bar */
.payouts-page >>> .v-progress-linear {
  background: rgba(255,255,255,0.06) !important;
}

/* Dividers */
.payouts-page >>> .v-divider {
  border-color: rgba(255,255,255,0.06) !important;
}

/* Dialogs */
.payouts-page >>> .v-dialog .v-card {
  background: #232838 !important;
}
.payouts-page >>> .v-dialog .v-card__actions .v-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600 !important;
}

/* Signature pad — keep light in dialog (works on white card) */
.signature-container {
  border: 2px dashed #ccc;
  border-radius: 8px;
  padding: 8px;
  background-color: #f9f9f9;
}
.signature-pad {
  border-radius: 4px;
  background-color: white;
}

/* Breakdown table inside expanded settlement row — dark */
.breakdown-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  margin-top: 6px;
  color: #fff;
}
.breakdown-table th,
.breakdown-table td {
  padding: 8px 10px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
}
.breakdown-table th {
  font-weight: 600;
  color: #6E7691;
  background: #181C28;
  text-align: left;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.breakdown-table tr.totals-row td {
  border-top: 2px solid rgba(255,255,255,0.12);
  background: rgba(41, 98, 255, 0.08);
  font-weight: 700;
  color: #fff;
}
</style>
